import { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useSearchParams } from 'react-router-dom';
import { SchemaOrg } from '@/components/seo/SchemaOrg';
import { getBookingTheme } from '@/lib/bookingThemes';
import { supabase } from '@/lib/supabase';
import { readFunctionInvokeErrorCode } from '@/lib/supabase-functions';
import { mpPreferenceErrorMessage } from '@/lib/booking/mpPreferenceErrors';
import {
  canCreateBookingDirectly,
  depositBadgeLabel,
  effectiveDepositAmount,
  isDepositRequired,
  isMpAvailable,
  type PaymentConfigLike,
} from '@/lib/booking/depositGate';
import { addDays, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Check,
  ChevronDown,
  Facebook,
  Globe2,
  Clock,
  Instagram,
  Loader2,
  Lock,
  MapPin,
  Music2,
  Scissors,
  Sparkles,
  Star,
  X,
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { usePublicBooking } from '@/hooks/usePublicBooking';
import { readCategoryOrderSettings, sortCategoryNamesByOrder } from '@/lib/service-category-order';
import { minutesToTime, getIndustryRule } from '@/lib/smartSlots';
import { formatCurrency, getPhonePlaceholder, resolveOrganizationLocale } from '@/lib/locale';
import { buildWhatsappUrl, getTenantSocialLinks } from '@/lib/socialLinks';
import type { ScoredSlot } from '@/lib/smartSlots';
import { ProProfilePage } from '@/components/booking/ProProfilePage';
import type { PublicService, ClientHistory, PublicProfessional } from '@/hooks/usePublicBooking';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAY_MAP: Record<number, string> = {
  0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday',
  4: 'thursday', 5: 'friday', 6: 'saturday',
};
const DAY_ABBR: Record<number, string> = {
  0: 'Do', 1: 'Lu', 2: 'Ma', 3: 'Mi', 4: 'Ju', 5: 'Vi', 6: 'Sá',
};

function formatDur(min: number) {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

function buildPhoneLookupCandidates(phone: string, locale: ReturnType<typeof resolveOrganizationLocale>) {
  const raw = phone.trim();
  const digits = raw.replace(/\D/g, '');
  const prefixDigits = locale.phone_prefix.replace(/\D/g, '');
  const candidates = new Set<string>();

  if (raw.length >= 7) candidates.add(raw);
  if (digits.length >= 7) candidates.add(digits);
  if (digits.length >= 7 && prefixDigits) {
    candidates.add(`+${digits.startsWith(prefixDigits) ? digits : `${prefixDigits}${digits}`}`);
  }

  return [...candidates].filter((candidate) => candidate.replace(/\D/g, '').length >= 7);
}

interface SelectedSvc {
  serviceId: string;
  category: string;
}

interface BookingDraft {
  selectedSvcs: SelectedSvc[];
  selectedProId: string;
  selectedDate: string; // ISO string
  selectedSlot: number;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  depositAmount?: number;
  serviceDurations?: Record<string, number>; // serviceId → duration_minutes
  reservationId?: string | null;
  campaignCode?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

// La opcion "sin preferencia" de profesional esta deshabilitada temporalmente en el flujo de booking publico.
const NO_PREFERENCE_OPTION_ENABLED = false;

export default function PublicBookingPage() {
  const { salonCode } = useParams<{ salonCode: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    salon, services, professionals,
    loading, error,
    slotsStale, setSlotsStale,
    getAvailableSlots, lookupClientHistory, bookAppointment, addToWaitlist,
    reserveSlot, releaseSlotReservation,
  } = usePublicBooking(salonCode ?? '');

  // Landing
  const [landingCatTab, setLandingCatTab] = useState<string>('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Sheet
  const [sheetOpen, setSheetOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  // Professional profile sheet
  const [profilePro, setProfilePro] = useState<PublicProfessional | null>(null);

  // Step 1 — Services
  const [selectedSvcs, setSelectedSvcs] = useState<SelectedSvc[]>([]);
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  // Step 3 — Professional
  const [selectedProId, setSelectedProId] = useState<string>('');

  // Step 4 — Date/Time
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [slots, setSlots] = useState<ScoredSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [dayFull, setDayFull] = useState(false);
  // No pudimos determinar la hora autoritativa: distinto de "sin cupo".
  const [slotsUnavailable, setSlotsUnavailable] = useState(false);

  // Waitlist
  const [waitlistNotes, setWaitlistNotes] = useState('');
  const [submittingWaitlist, setSubmittingWaitlist] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  // Map geocoding fallback
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Auto-booking after MP redirect
  const [pendingMpDraft, setPendingMpDraft] = useState<BookingDraft | null>(null);

  // Opening hours popover
  const [showHours, setShowHours] = useState(false);
  const hoursRef = useRef<HTMLDivElement>(null);

  // Expanded service descriptions
  const [expandedDescs, setExpandedDescs] = useState<Set<string>>(new Set());
  const toggleDesc = (id: string) => setExpandedDescs(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });

  // Step 2 — Client identification (V3: moved before professional)
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [campaignCode, setCampaignCode] = useState('');
  const [campaignCodeCheck, setCampaignCodeCheck] = useState<{
    valid: boolean;
    reason?: 'not_found' | 'expired' | 'empty';
    discount_type?: 'percentage' | 'fixed' | null;
    discount_value?: number | null;
  } | null>(null);
  const [checkingCampaignCode, setCheckingCampaignCode] = useState(false);
  const [clientHistory, setClientHistory] = useState<ClientHistory | null>(null);
  const [lookingUpClient, setLookingUpClient] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionPromptSeenKey, setSuggestionPromptSeenKey] = useState<string | null>(null);

  // Booking
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Fase 4 — pre-reserva
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [reservationExpiresAt, setReservationExpiresAt] = useState<Date | null>(null);
  const [reservationCountdown, setReservationCountdown] = useState<string | null>(null);

  // MercadoPago
  const [mpRedirecting, setMpRedirecting] = useState(false);
  const [mpPaymentDone, setMpPaymentDone] = useState(false);
  // Distinto de `mpPaymentDone`: aquel se activa con el query string de
  // retorno de MercadoPago, ANTES de verificar nada. Este solo se activa con
  // el resultado de mp-verify-deposit-and-book, que comprueba `approved`
  // contra /v1/payments antes de crear la cita.
  const [depositPaidConfirmed, setDepositPaidConfirmed] = useState(false);
  const [mpCollectionId, setMpCollectionId] = useState<string | null>(null);
  const bookingDraftKey = `mestly_booking_draft_${salonCode}`;
  const orgLocale = useMemo(() => resolveOrganizationLocale(salon), [salon]);

  // ─── Derived ────────────────────────────────────────────────────────────────

  const serviceById = useMemo(
    () => Object.fromEntries(services.map((s) => [s.id, s])),
    [services]
  );

  const categories = useMemo(() => {
    const map = new Map<string, PublicService[]>();
    for (const svc of services) {
      if (!map.has(svc.category)) map.set(svc.category, []);
      map.get(svc.category)!.push(svc);
    }
    return map;
  }, [services]);

  const orderedCategoryEntries = useMemo(() => {
    const order = readCategoryOrderSettings((salon?.settings as Record<string, unknown> | null) ?? null);
    const names = sortCategoryNamesByOrder(Array.from(categories.keys()), order);
    return names.map((name) => [name, categories.get(name) ?? []] as const);
  }, [categories, salon?.settings]);

  const categoryOrderIndex = useMemo(
    () => new Map(orderedCategoryEntries.map(([name], index) => [name, index])),
    [orderedCategoryEntries]
  );

  // Categorías filtradas para el sheet de booking.
  // Si hay un profesional pre-seleccionado con service_ids configurados,
  // solo mostramos los servicios que ese profesional puede realizar.
  const bookingCategoryEntries = useMemo(() => {
    const preSelectedPro =
      selectedProId && selectedProId !== 'any'
        ? professionals.find((p) => p.id === selectedProId)
        : null;

    if (!preSelectedPro || preSelectedPro.service_ids.length === 0) {
      return orderedCategoryEntries;
    }

    return orderedCategoryEntries
      .map(([cat, svcs]) => [cat, svcs.filter((s) => preSelectedPro.service_ids.includes(s.id))] as const)
      .filter(([, svcs]) => svcs.length > 0);
  }, [orderedCategoryEntries, selectedProId, professionals]);

  const selectedCategories = useMemo(
    () => new Set(selectedSvcs.map((s) => s.category)),
    [selectedSvcs]
  );

  const totalDuration = useMemo(
    () => selectedSvcs.reduce((sum, s) => sum + (serviceById[s.serviceId]?.duration_minutes ?? 0), 0),
    [selectedSvcs, serviceById]
  );
  const totalPrice = useMemo(
    () => selectedSvcs.reduce((sum, s) => sum + (serviceById[s.serviceId]?.price ?? 0), 0),
    [selectedSvcs, serviceById]
  );

  const depositAmount = useMemo(() => {
    const cfg = (salon?.settings as Record<string, unknown>)?.payment_config as
      | { deposit_type?: string; deposit_value?: number }
      | undefined;
    let base = 0;
    if (cfg?.deposit_type === 'fixed') base = cfg.deposit_value ?? 0;
    else if (cfg?.deposit_type === 'percentage')
      base = Math.round(totalPrice * (cfg.deposit_value ?? 0) / 100);
    // Fase 4: clients with ≥2 no-shows must pay at least 20% deposit
    if (clientHistory && clientHistory.no_show_count >= 2) {
      const noShowMin = Math.round(totalPrice * 0.20);
      return Math.max(base, noShowMin);
    }
    return base;
  }, [salon, totalPrice, clientHistory]);

  // `require_deposit` se guardaba en Pagos y anticipos pero NADIE lo leía en el
  // booking público: el anticipo se calculaba solo desde deposit_type/value.
  // Es la señal de producto que decide si la reserva puede crearse sin pago.
  const paymentConfig = useMemo(
    () => (salon?.settings as Record<string, unknown>)?.payment_config as PaymentConfigLike | undefined,
    [salon],
  );
  const depositRequired = useMemo(
    () => isDepositRequired(paymentConfig, depositAmount),
    [paymentConfig, depositAmount],
  );
  // Lo que se guarda en la cita. Con el anticipo no obligatorio es 0: nunca se
  // cobra, así que no puede quedar registrado como pendiente.
  const effectiveDeposit = useMemo(
    () => effectiveDepositAmount(paymentConfig, depositAmount),
    [paymentConfig, depositAmount],
  );

  const mpConfig = useMemo(() => {
    const cfg = (salon?.settings as Record<string, unknown>)?.mp_checkout as
      | { is_active?: boolean; mp_public_key?: string }
      | undefined;
    return {
      isActive: !!(cfg?.is_active && cfg?.mp_public_key),
      publicKey: cfg?.mp_public_key ?? '',
      isSandbox: (cfg?.mp_public_key ?? '').startsWith('TEST-'),
    };
  }, [salon]);

  // Disponibilidad real del cobro online. Si el anticipo es obligatorio y esto
  // es false, la reserva NO se crea: antes caía al botón de reserva directa,
  // que además decía "Pagar abono $X" mientras reservaba sin cobrar nada.
  const mpAvailable = isMpAvailable(mpConfig);

  const eligiblePros = useMemo(() => {
    const ids = selectedSvcs.map((s) => s.serviceId);
    return professionals.filter((p) => ids.every((id) => p.service_ids.includes(id)));
  }, [professionals, selectedSvcs]);

  const confirmedPro = useMemo(
    () => (confirmed && selectedProId ? professionals.find((p) => p.id === selectedProId) ?? null : null),
    [confirmed, selectedProId, professionals]
  );

  const suggestionPromptKey = useMemo(() => {
    const serviceKey = [...selectedSvcs.map((svc) => svc.serviceId)].sort().join(',');
    return `${clientPhone.trim()}::${serviceKey}`;
  }, [clientPhone, selectedSvcs]);

  const suggestedServices = useMemo(() => {
    if (!clientPhone.trim() || selectedSvcs.length === 0 || selectedCategories.size >= 3) return [];

    const selectedServiceIds = new Set(selectedSvcs.map((svc) => svc.serviceId));
    const visitCounts = clientHistory?.service_visit_counts ?? {};

    const compatibleCandidates = services
      .filter((svc) =>
        svc.is_active &&
        svc.is_bookable_online &&
        !selectedServiceIds.has(svc.id) &&
        !selectedCategories.has(svc.category)
      )
      .filter((svc) =>
        professionals.some((pro) => [...selectedServiceIds, svc.id].every((serviceId) => pro.service_ids.includes(serviceId)))
      );

    const sortByPriority = (a: PublicService, b: PublicService) => {
      const countDiff = (visitCounts[b.id] ?? 0) - (visitCounts[a.id] ?? 0);
      if (countDiff !== 0) return countDiff;
      const categoryDiff = (categoryOrderIndex.get(a.category) ?? 999) - (categoryOrderIndex.get(b.category) ?? 999);
      if (categoryDiff !== 0) return categoryDiff;
      if (a.price !== b.price) return a.price - b.price;
      return a.duration_minutes - b.duration_minutes;
    };

    const repeated = compatibleCandidates.filter((svc) => (visitCounts[svc.id] ?? 0) > 0).sort(sortByPriority);
    const novel = compatibleCandidates.filter((svc) => (visitCounts[svc.id] ?? 0) === 0).sort(sortByPriority);
    const pool = [...repeated, ...novel];
    const pickedCategories = new Set<string>();

    return pool.filter((svc) => {
      if (pickedCategories.has(svc.category)) return false;
      pickedCategories.add(svc.category);
      return true;
    }).slice(0, 3);
  }, [categoryOrderIndex, clientHistory?.service_visit_counts, clientPhone, professionals, selectedCategories, selectedSvcs, services]);

  const suggestionMode = useMemo(
    () => suggestedServices.some((svc) => (clientHistory?.service_visit_counts?.[svc.id] ?? 0) > 0) ? 'repeat' : 'discover',
    [clientHistory?.service_visit_counts, suggestedServices]
  );

  // ─── Theme CSS variables ─────────────────────────────────────────────────────
  const themeVars = useMemo(() => {
    const theme = getBookingTheme((salon?.settings as Record<string, unknown>)?.booking_theme as string | undefined);
    return theme.vars as unknown as React.CSSProperties;
  }, [salon?.settings]);

  const socialLinks = useMemo(
    () => getTenantSocialLinks((salon?.settings as Record<string, unknown> | null) ?? null),
    [salon?.settings]
  );

  const whatsappUrl = useMemo(
    () => buildWhatsappUrl(socialLinks.whatsapp ?? salon?.phone ?? undefined),
    [salon?.phone, socialLinks.whatsapp]
  );

  const socialButtons = useMemo(() => {
    const entries = [
      socialLinks.instagram ? { key: 'instagram', href: socialLinks.instagram, label: 'Instagram', icon: Instagram } : null,
      socialLinks.facebook ? { key: 'facebook', href: socialLinks.facebook, label: 'Facebook', icon: Facebook } : null,
      socialLinks.tiktok ? { key: 'tiktok', href: socialLinks.tiktok, label: 'TikTok', icon: Music2 } : null,
      socialLinks.website ? { key: 'website', href: socialLinks.website, label: 'Sitio web', icon: Globe2 } : null,
    ];

    return entries.filter(Boolean) as Array<{
      key: string;
      href: string;
      label: string;
      icon: typeof Instagram;
    }>;
  }, [socialLinks.facebook, socialLinks.instagram, socialLinks.tiktok, socialLinks.website]);

  const heroImgOpacity = useMemo(() => {
    const theme = getBookingTheme((salon?.settings as Record<string, unknown>)?.booking_theme as string | undefined);
    return parseFloat(theme.vars['--pb-hero-img-opacity'] ?? '0.6');
  }, [salon?.settings]);

  // ─── V3: Returning client lookup (debounced phone) ──────────────────────────

  useEffect(() => {
    const lookupCandidates = buildPhoneLookupCandidates(clientPhone, orgLocale);
    if (lookupCandidates.length === 0) {
      setClientHistory(null);
      setLookingUpClient(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLookingUpClient(true);
      let history: ClientHistory | null = null;
      for (const candidate of lookupCandidates) {
        history = await lookupClientHistory(candidate);
        if (history) break;
      }
      if (cancelled) return;
      setClientHistory(history);
      // Auto-fill name if field is empty and client was found
      if (history && !clientName.trim()) setClientName(history.clientName);
      setLookingUpClient(false);
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // Debounce de lookup de cliente
  // Intencional: debounce de lookup de cliente. lookupClientHistory es estable
  // (useCallback del hook). Solo debe dispararse al cambiar el teléfono.
  }, [clientPhone, clientName, lookupClientHistory, orgLocale]);

  // ─── Verificación del código de descuento (debounced) ───────────────────────
  // Antes el código solo se validaba dentro de book_appointment_safe(), al
  // confirmar la reserva -- para una cita con abono eso pasa DESPUÉS de que
  // el cliente ya pagó en MercadoPago. Esto avisa mientras todavía lo está
  // escribiendo, antes de llegar a pagar nada.
  useEffect(() => {
    const trimmed = campaignCode.trim();
    if (!trimmed || !salon?.id) {
      setCampaignCodeCheck(null);
      setCheckingCampaignCode(false);
      return;
    }
    let cancelled = false;
    setCheckingCampaignCode(true);
    const timer = setTimeout(async () => {
      const { data, error } = await supabase.rpc('validate_campaign_code', {
        p_salon_id: salon.id,
        p_code: trimmed,
      });
      if (cancelled) return;
      setCheckingCampaignCode(false);
      if (!error && data) setCampaignCodeCheck(data as typeof campaignCodeCheck);
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // Debounce de verificación de código -- mismo patrón que el lookup de
  // teléfono de arriba.
  }, [campaignCode, salon?.id]);

  useEffect(() => {
    if (showSuggestionModal && suggestedServices.length === 0) {
      setShowSuggestionModal(false);
      setStep(3);
    }
  }, [showSuggestionModal, suggestedServices.length]);

  // ─── Auto-refresh slots when stale ──────────────────────────────────────────

  useEffect(() => {
    if (selectedProId && !eligiblePros.some((pro) => pro.id === selectedProId)) {
      setSelectedProId('');
      setSelectedDate(null);
      setSelectedSlot(null);
      setSlots([]);
      setDayFull(false);
      if (reservationId) {
        void releaseSlotReservation(reservationId);
        setReservationId(null);
        setReservationExpiresAt(null);
      }
    }
  }, [eligiblePros, releaseSlotReservation, reservationId, selectedProId]);

  useEffect(() => {
    if (!slotsStale || step !== 4 || !selectedDate || totalDuration === 0) return;
    const proId = selectedProId;
    if (!proId) return;
    setLoadingSlots(true);
    toast('Actualizando disponibilidad...', { icon: '🔄' });
    void getAvailableSlots(proId, selectedDate, totalDuration, {
      clientPhone: clientPhone.trim() || null,
    }).then(({ slots: newSlots, dayFull: full }) => {
      setSlots(newSlots);
      setDayFull(full);
      if (selectedSlot !== null && !newSlots.some((s) => s.startMin === selectedSlot)) {
        setSelectedSlot(null);
        toast.warning('El horario seleccionado ya no está disponible');
      }
      setLoadingSlots(false);
      setSlotsStale(false);
    });
  }, [slotsStale, step, selectedDate, totalDuration, selectedProId, eligiblePros, clientPhone,
    selectedSlot, getAvailableSlots, setSlotsStale]);

  useEffect(() => {
    if (sheetOpen) return;
    if (!reservationId) return;
    void releaseSlotReservation(reservationId);
    setReservationId(null);
    setReservationExpiresAt(null);
  }, [releaseSlotReservation, reservationId, sheetOpen]);

  useEffect(() => {
    if (!bookingCategoryEntries.length) {
      setOpenCategory(null);
      return;
    }

    setOpenCategory((prev) => {
      if (prev && bookingCategoryEntries.some(([categoryName]) => categoryName === prev)) {
        return prev;
      }

      return bookingCategoryEntries[0][0];
    });
  }, [bookingCategoryEntries]);

  // ─── MercadoPago return detection ───────────────────────────────────────────

  useEffect(() => {
    const mpStatus = searchParams.get('mp_status');
    if (!mpStatus) return;

    const draftId = searchParams.get('draft_id');

    // Clean up URL so refreshing doesn't re-trigger
    const next = new URLSearchParams(searchParams);
    next.delete('mp_status');
    next.delete('draft_id');
    setSearchParams(next, { replace: true });

    const restoreDraft = (draft: BookingDraft) => {
      setSelectedSvcs(draft.selectedSvcs);
      setSelectedProId(draft.selectedProId);
      setSelectedDate(new Date(draft.selectedDate));
      setSelectedSlot(draft.selectedSlot);
      setClientName(draft.clientName);
      setClientPhone(draft.clientPhone);
      setClientEmail(draft.clientEmail);
      setCampaignCode(draft.campaignCode ?? '');
      setSheetOpen(true);
      setStep(5);
    };

    void (async () => {
      // sessionStorage es el camino rápido de siempre. Si no sobrevivió el
      // viaje a MercadoPago y de vuelta (pasa sobre todo desde el navegador
      // interno de WhatsApp — incidente real confirmado), se recupera el
      // mismo draft desde el respaldo del servidor via draft_id.
      let raw = sessionStorage.getItem(bookingDraftKey);
      if (!raw && draftId) {
        try {
          const { data } = await supabase.rpc('get_booking_deposit_draft', { p_id: draftId });
          if (data) raw = JSON.stringify(data);
        } catch { /* ignore -- cae al mensaje de error de siempre si tampoco hay draftId */ }
      }

      if (mpStatus === 'approved') {
        if (!raw) {
          toast.error('No se encontró tu reserva. Por favor intenta de nuevo.');
          return;
        }
        try {
          // Capturar el ID de pago de MercadoPago para trazabilidad financiera
          const collectionId = searchParams.get('collection_id') ?? searchParams.get('payment_id');
          if (collectionId) setMpCollectionId(collectionId);

          const draft = JSON.parse(raw) as BookingDraft;
          restoreDraft(draft);
          setMpPaymentDone(true);
          sessionStorage.removeItem(bookingDraftKey);
          // Trigger auto-booking — runs once salon is loaded (see useEffect below)
          setPendingMpDraft(draft);
        } catch {
          toast.error('Error al recuperar tu reserva');
        }
      } else if (mpStatus === 'rejected') {
        toast.error('El pago fue rechazado. Por favor intenta con otro método.');
        if (raw) {
          try { restoreDraft(JSON.parse(raw) as BookingDraft); } catch { /* ignore */ }
        }
      } else if (mpStatus === 'pending') {
        toast('Tu pago está en revisión. Te notificaremos cuando se confirme.', { icon: '⏳' });
        if (raw) {
          try { restoreDraft(JSON.parse(raw) as BookingDraft); } catch { /* ignore */ }
          sessionStorage.removeItem(bookingDraftKey);
        }
      }
    })();
  // Lectura de retorno de MercadoPago
  // Intencional: mount-only. Lee search params de MercadoPago (mpStatus)
  // una sola vez al cargar la página. Reejecutar causaría toasts duplicados.
  }, [bookingDraftKey, searchParams, setSearchParams]);

  // ─── Clic desde campaña de WhatsApp (?ref=<campaign_id>) ─────────────────────
  // El botón dinámico de Meta solo admite una variable, ya usada por
  // salon_code -- por eso el clic no pasa por una página de redirección
  // propia (a diferencia de email, ver CampaignLinkRedirectPage.tsx) sino
  // que se detecta acá mismo. log_campaign_click registra el clic y, si el
  // código de la campaña sigue vigente, lo devuelve para no obligar al
  // cliente a escribirlo a mano.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (!ref) return;

    const next = new URLSearchParams(searchParams);
    next.delete('ref');
    setSearchParams(next, { replace: true });

    void (async () => {
      const { data: code, error } = await supabase.rpc('log_campaign_click', {
        p_campaign_id: ref,
        p_channel: 'whatsapp',
      });
      if (!error && code) {
        setCampaignCode((current) => current || (code as string));
      }
    })();
  // Intencional: mount-only, mismo patron que el efecto de mp_status arriba.
  }, [searchParams, setSearchParams]);

  // ─── Auto-booking after MercadoPago approval ─────────────────────────────────
  useEffect(() => {
    if (!pendingMpDraft || !salon) return;
    setPendingMpDraft(null); // consume it once

    const draft = pendingMpDraft;
    setBooking(true);
    setBookingError(null);

    bookAppointment({
      selectedServices: draft.selectedSvcs.map(s => ({
        id: s.serviceId,
        duration_minutes: draft.serviceDurations?.[s.serviceId] ?? serviceById[s.serviceId]?.duration_minutes ?? 0,
      })),
      professionalId: draft.selectedProId,
      date: new Date(draft.selectedDate),
      startMinute: draft.selectedSlot,
      clientName: draft.clientName,
      clientPhone: draft.clientPhone,
      clientEmail: draft.clientEmail || undefined,
      depositAmount: draft.depositAmount ?? depositAmount,
      // `depositPaid` no es el campo que usePublicBooking realmente chequea
      // para tomar el camino verificado (mp-verify-deposit-and-book) -- ese
      // es `mpPaymentDone` + `mpPaymentId`, igual que ya hace handleBook() más
      // abajo. Con el campo equivocado, esta reserva cae siempre en la rama
      // sin verificar (deposit_paid=false, mp_payment_id=null en la fila real)
      // aunque el pago sí se haya confirmado -- bug real encontrado probando
      // en Producción: el abono se pagó pero la cita quedó "pend.".
      mpPaymentDone: true, // MP payment was confirmed before reaching this point
      mpPaymentId: mpCollectionId,
      reservationId: draft.reservationId ?? null,
      campaignCode: draft.campaignCode || undefined,
    }).then(result => {
      setBooking(false);
      if (result.success) {
        setDepositPaidConfirmed(Boolean(result.depositPaid));
        setConfirmed(true);
      } else if (result.error === 'SLOT_TAKEN') {
        setBookingError('El horario fue tomado mientras procesabas el pago. Comunícate con el negocio para coordinar.');
        toast.error('Horario no disponible — el negocio te contactará', { duration: 10000 });
      } else if (result.error === 'BOOKING_TOO_SOON') {
        setBookingError('Tu pago fue recibido, pero ese horario ya está demasiado próximo para confirmarse online. Comunícate con el negocio para coordinar.');
        toast.error('Pago recibido — comunícate con el negocio para confirmar tu cita', { duration: 10000 });
      } else if (result.error === 'DEPOSIT_NOT_VERIFIED') {
        // El anticipo no quedó confirmado: no hay reserva y no se afirma pago.
        setBookingError('No pudimos confirmar el pago de tu abono, así que la reserva no se creó. Si el cobro se realizó, contacta al negocio; si no, puedes intentar el pago nuevamente.');
        toast.error('No pudimos confirmar tu pago', { duration: 10000 });
      } else if (result.error === 'PHONE_LIMIT') {
        setBookingError('Ya tienes citas activas en este salón.');
      } else if (result.error === 'INVALID_CAMPAIGN_CODE') {
        setBookingError('Tu pago fue recibido pero el código de descuento no es válido. Comunícate con el negocio.');
        toast.error('Pago recibido — comunícate con el negocio para confirmar tu cita', { duration: 10000 });
      } else if (result.error === 'CAMPAIGN_CODE_EXPIRED') {
        setBookingError('Tu pago fue recibido pero el código de descuento ya venció. Comunícate con el negocio.');
        toast.error('Pago recibido — comunícate con el negocio para confirmar tu cita', { duration: 10000 });
      } else {
        setBookingError('Tu pago fue recibido pero hubo un error al confirmar la cita. Comunícate con el negocio.');
        toast.error('Pago recibido — comunícate con el negocio para confirmar tu cita', { duration: 10000 });
      }
    });
  // Intencional: solo se ejecuta cuando pendingMpDraft y salon están disponibles
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMpDraft, salon]);

  // ─── Fase 4: countdown for pre-reservation ──────────────────────────────────

  useEffect(() => {
    if (!reservationExpiresAt) { setReservationCountdown(null); return; }
    const tick = () => {
      const diff = Math.max(0, reservationExpiresAt.getTime() - Date.now());
      if (diff === 0) { setReservationCountdown(null); setReservationId(null); setReservationExpiresAt(null); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setReservationCountdown(`${m}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [reservationExpiresAt]);

  // Geocode address via Nominatim when no lat/lng stored
  useEffect(() => {
    if (!salon) return;
    if (salon.latitude && salon.longitude) {
      setMapCoords({ lat: salon.latitude, lng: salon.longitude });
      return;
    }
    if (!salon.address) return;
    let cancelled = false;

    // Strip office/floor notation that confuses geocoders ("Of 1001", "Oficina 3", "Piso 2")
    const cleanAddress = salon.address
      .replace(/\b(of\.?|oficina|piso|depto\.?|departamento|local|suite)\s*\d+\w*/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    // Add country context using country_code if available
    const countryMap: Record<string, string> = {
      CL: 'Chile', AR: 'Argentina', MX: 'Mexico', CO: 'Colombia',
      PE: 'Peru', UY: 'Uruguay', EC: 'Ecuador', BO: 'Bolivia',
    };
    const country = salon.country_code ? (countryMap[salon.country_code.toUpperCase()] ?? '') : '';
    const query = country ? `${cleanAddress}, ${country}` : cleanAddress;

    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&addressdetails=0`)
      .then(r => r.json())
      .then((results: Array<{ lat: string; lon: string }>) => {
        if (cancelled || !results?.[0]) return;
        setMapCoords({ lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) });
      })
      .catch(() => { /* silent — placeholder shows instead */ });
    return () => { cancelled = true; };
  }, [salon, salon?.address, salon?.latitude, salon?.longitude, salon?.country_code]);

  // Close hours popover on outside click
  useEffect(() => {
    if (!showHours) return;
    const handler = (e: MouseEvent) => {
      if (hoursRef.current && !hoursRef.current.contains(e.target as Node)) setShowHours(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showHours]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  async function loadSlots(date: Date, proId: string) {
    if (!proId) {
      setSlots([]);
      setSelectedSlot(null);
      setDayFull(false);
      return;
    }
    setLoadingSlots(true);
    setSlots([]);
    setSelectedSlot(null);
    setDayFull(false);
    setWaitlistDone(false);
    const v3Options = {
      clientPreferredRange: clientHistory?.preferred_hour_range ?? null,
      clientPhone: clientPhone.trim() || null,
    };
    try {
      const result = await getAvailableSlots(proId, date, totalDuration, v3Options);
      const fetched = result.slots;
      const full = result.dayFull;
      setSlots(fetched);
      setDayFull(full);
      setSlotsUnavailable(Boolean(result.clockUnavailable));
    } finally {
      setLoadingSlots(false);
    }
  }

  async function handleDateSelect(date: Date) {
    setSelectedDate(date);
    await loadSlots(date, selectedProId);
  }

  // Fase 4: called when the client picks a time slot in Step 4
  async function handleSlotSelect(slotStartMin: number) {
    // Release any previous reservation
    if (reservationId) {
      void releaseSlotReservation(reservationId);
      setReservationId(null);
      setReservationExpiresAt(null);
    }
    setSelectedSlot(slotStartMin);
    if (!selectedDate || !clientPhone.trim()) return;
    const proId = selectedProId;
    if (!proId) return;
    const resId = await reserveSlot({
      professionalId: proId,
      date: selectedDate,
      slotStartMin,
      slotEndMin: slotStartMin + totalDuration,
      clientPhone,
    });
    if (resId) {
      setReservationId(resId);
      setReservationExpiresAt(new Date(Date.now() + 5 * 60 * 1000));
    }
  }

  async function handleMpPayment() {
    if (!salon?.id || !selectedDate || selectedSlot === null) return;
    setMpRedirecting(true);
    try {
      const draft: BookingDraft = {
        selectedSvcs,
        selectedProId,
        selectedDate: selectedDate.toISOString(),
        selectedSlot,
        clientName,
        clientPhone,
        clientEmail,
        depositAmount: effectiveDeposit,
        serviceDurations: Object.fromEntries(
          selectedSvcs.map(s => [s.serviceId, serviceById[s.serviceId]?.duration_minutes ?? 0])
        ),
        reservationId: reservationId ?? null,
        campaignCode: campaignCode || undefined,
      };
      sessionStorage.setItem(bookingDraftKey, JSON.stringify(draft));

      const serviceNames = selectedSvcs
        .map((s) => serviceById[s.serviceId]?.name)
        .filter(Boolean)
        .join(', ');

      // Mestly opera principalmente en Chile (Inversiones MyM SpA); si el negocio
      // no configuro explicitamente su moneda, CLP es el default correcto, no ARS.
      const salonCurrency = (salon.settings as Record<string, unknown>)?.currency_code as string | undefined;
      const { data, error } = await supabase.functions.invoke('mp-create-preference', {
        body: {
          salon_id: salon.id,
          amount: effectiveDeposit,
          title: `Anticipo reserva — ${serviceNames}`,
          client_name: clientName,
          client_email: clientEmail || undefined,
          back_url_base: `${window.location.origin}/book/${salonCode}`,
          currency_id: salonCurrency ?? 'CLP',
          // Respaldo server-side del mismo draft que ya se guarda arriba en
          // sessionStorage -- sessionStorage no sobrevive de forma confiable
          // el viaje a MercadoPago y de vuelta (ver mp_status return
          // detection más abajo).
          draft,
        },
      });

      // El negocio ya no admite esta reserva: se corta ANTES de MercadoPago,
      // así que no hay ningún cobro que deshacer.
      //
      // Un rechazo del precheck llega como HTTP 400/503, y supabase-js lo
      // convierte en FunctionsHttpError con `data === null`. Leer solo
      // `error.message` daba "Edge Function returned a non-2xx status code":
      // el código de negocio viaja en el BODY, accesible vía `error.context`.
      const rejectBooking = (message: string) => {
        sessionStorage.removeItem(bookingDraftKey);
        setBookingError(message);
        setMpRedirecting(false);
      };

      if (error) {
        const message = mpPreferenceErrorMessage(await readFunctionInvokeErrorCode(error));
        if (message) { rejectBooking(message); return; }
        throw error; // sin código legible: se conserva el fallback genérico
      }

      if (data?.error) {
        // Mismo mapeo para una respuesta 2xx que traiga el error en el body.
        const message = mpPreferenceErrorMessage(data.error as string);
        if (message) { rejectBooking(message); return; }
        throw new Error(data.error as string);
      }

      const checkoutUrl = mpConfig.isSandbox
        ? (data.sandbox_init_point as string)
        : (data.init_point as string);

      if (!checkoutUrl) throw new Error('No se obtuvo URL de pago');
      window.location.href = checkoutUrl;
    } catch (err) {
      sessionStorage.removeItem(bookingDraftKey);
      toast.error(err instanceof Error ? err.message : 'Error al iniciar el pago con MercadoPago');
      setMpRedirecting(false);
    }
  }

  async function handleBook() {
    if (!selectedDate || selectedSlot === null) return;

    // Defensa en profundidad: aunque un cambio futuro en el render deje
    // alcanzable este camino, una reserva con anticipo obligatorio no se crea
    // sin pago verificado. El render es una comodidad, no la garantía.
    if (!canCreateBookingDirectly({ paymentConfig, depositAmount, mpPaymentDone })) {
      setBookingError('El pago online no está disponible temporalmente. Intenta nuevamente en unos minutos o contacta al negocio.');
      return;
    }
    setBooking(true);
    setBookingError(null);

    const result = await bookAppointment({
      selectedServices: selectedSvcs.map((s) => ({
        id: s.serviceId,
        duration_minutes: serviceById[s.serviceId]?.duration_minutes ?? 0,
      })),
      professionalId: selectedProId,
      date: selectedDate,
      startMinute: selectedSlot,
      clientName,
      clientPhone,
      clientEmail: clientEmail || undefined,
      depositAmount: effectiveDeposit,
      reservationId,
      mpPaymentDone,
      mpPaymentId: mpCollectionId,
      campaignCode: campaignCode || undefined,
    });

    setBooking(false);

    if (result.success) {
      // Clear reservation state (already deleted server-side by the RPC)
      setReservationId(null);
      setReservationExpiresAt(null);
      setDepositPaidConfirmed(Boolean(result.depositPaid));
      setConfirmed(true);
    } else if (result.error === 'SLOT_TAKEN') {
      setReservationId(null);
      setReservationExpiresAt(null);
      setStep(4);
      setBookingError('Ese horario ya fue tomado. Por favor elige otro.');
      await loadSlots(selectedDate, selectedProId);
    } else if (result.error === 'BOOKING_TOO_SOON') {
      // El horario dejó de ser reservable mientras el cliente completaba el
      // formulario. Se vuelve al paso 4 (no se reinicia el flujo, así los
      // datos ya escritos se conservan) y se recargan los slots, que ahora
      // vendrán filtrados por el umbral vigente.
      setReservationId(null);
      setReservationExpiresAt(null);
      setStep(4);
      setBookingError('Este horario ya está demasiado próximo para reservar online. Te mostramos las próximas opciones disponibles.');
      await loadSlots(selectedDate, selectedProId);
    } else if (result.error === 'SLOT_RESERVED') {
      setReservationId(null);
      setReservationExpiresAt(null);
      setStep(4);
      setBookingError('Ese horario está siendo reservado por otra persona en este momento. Por favor elige otro.');
      await loadSlots(selectedDate, selectedProId);
    } else if (result.error === 'PHONE_LIMIT') {
      setBookingError('Ya tienes 2 citas activas en este salón. Cancela una para reservar otra.');
    } else if (result.error === 'IP_LIMIT') {
      setBookingError('Has alcanzado el límite de reservas desde esta conexión. Intenta más tarde.');
    } else if (result.error === 'INVALID_CAMPAIGN_CODE') {
      setBookingError('Este código no es válido. Puedes borrarlo y reservar igual.');
    } else if (result.error === 'CAMPAIGN_CODE_EXPIRED') {
      setBookingError('Este código ya venció. Puedes borrarlo y reservar igual.');
    } else {
      setBookingError(result.error ?? 'Error al reservar. Intenta de nuevo.');
    }
  }

  async function handleWaitlist() {
    if (!selectedDate || !salon) return;
    setSubmittingWaitlist(true);
    const result = await addToWaitlist({
      professionalId: selectedProId || null,
      date: selectedDate,
      clientName,
      clientPhone,
      clientEmail,
      serviceIds: selectedSvcs.map((s) => s.serviceId),
      notes: waitlistNotes,
    });
    setSubmittingWaitlist(false);
    if (result.success) {
      setWaitlistDone(true);
    } else {
      toast.error(result.error ?? 'No se pudo agregar a la lista de espera.');
    }
  }

  function isDayOpen(date: Date): boolean {
    if (!salon?.opening_hours) return true;
    const dayName = DAY_MAP[date.getDay()];
    const h = salon.opening_hours[dayName];
    return !!h?.open;
  }

  function toggleService(svc: PublicService) {
    setSelectedSvcs((prev) => {
      const existing = prev.find((s) => s.category === svc.category);
      if (existing) {
        if (existing.serviceId === svc.id) return prev.filter((s) => s.serviceId !== svc.id);
        return prev.map((s) => s.category === svc.category ? { serviceId: svc.id, category: svc.category } : s);
      }
      if (selectedCategories.size >= 3) return prev;
      return [...prev, { serviceId: svc.id, category: svc.category }];
    });
  }

  function handleContinueAfterClient() {
    // canContinueAfterClient también frena si escribió un código y todavía
    // no se verificó (o vino inválido) -- mejor frenarlo acá que dejarlo
    // pagar el abono y recién ahí enterarse de que el código no sirvió (ver
    // book_appointment_safe).
    if (!canContinueAfterClient) return;
    if (suggestedServices.length > 0 && suggestionPromptSeenKey !== suggestionPromptKey) {
      setSuggestionPromptSeenKey(suggestionPromptKey);
      setShowSuggestionModal(true);
      return;
    }
    setStep(3);
  }

  const canContinueAfterClient = !!clientName.trim() && !!clientPhone.trim()
    && !(campaignCode.trim() && (checkingCampaignCode || campaignCodeCheck?.valid === false));

  function handleAddSuggestedService(svc: PublicService) {
    toggleService(svc);
  }

  function openSheet(keepSvcs = false, presetProId?: string) {
    setSheetOpen(true);
    // If services were pre-selected on landing, go straight to step 2 (client ID)
    if (keepSvcs && selectedSvcs.length > 0) {
      setStep(2);
    } else {
      setStep(1);
      setSelectedSvcs([]);
    }
    setOpenCategory(null);
    // presetProId: cuando se abre desde "Reservar" en la ficha de un
    // profesional (tarjeta del equipo o ProProfilePage), antes esto se
    // pisaba siempre a '' acá mismo -- el profesional elegido se perdía al
    // instante y el paso 1 volvía a mostrar todos los servicios (el filtro
    // por profesional en bookingCategoryEntries ya existía, solo dependía
    // de que selectedProId sobreviviera hasta acá).
    setSelectedProId(presetProId ?? '');
    setSelectedDate(null);
    setSelectedSlot(null);
    setSlots([]);
    setClientName('');
    setClientPhone('');
    setClientEmail('');
    setClientHistory(null);
    setBookingError(null);
    setConfirmed(false);
    setMpPaymentDone(false);
    setPendingMpDraft(null);
    setDayFull(false);
    setWaitlistNotes('');
    setWaitlistDone(false);
    setShowSuggestionModal(false);
    setSuggestionPromptSeenKey(null);
    // Fase 4
    setReservationId(null);
    setReservationExpiresAt(null);
  }

  // ─── Loading / Error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ background: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 style={{ width: 32, height: 32, color: 'var(--pb-modal-text)', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#888', fontSize: 13 }}>Cargando...</p>
        </div>
        <style>{`@keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (error || !salon) {
    return (
      <div style={{ background: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <p style={{ fontSize: 40, marginBottom: 12 }}>😕</p>
          <p style={{ fontSize: 18, fontWeight: 700, color: 'var(--pb-modal-text)', marginBottom: 6 }}>{error ?? 'Salón no encontrado'}</p>
          <p style={{ fontSize: 13, color: '#888' }}>Verifica el enlace e intenta de nuevo.</p>
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>{salon.name} — Reserva tu turno online | Mestly</title>
        <meta name="description" content={`Reserva tu turno en ${salon.name}. ${salon.address ?? ''}`} />
        <meta property="og:title" content={`${salon.name} — Reserva online`} />
        <meta property="og:image" content={salon.cover_photo_url ?? 'https://www.mestly.app/og-default.jpg'} />
        <link rel="canonical" href={`https://www.mestly.app/book/${salon.salon_code}`} />
      </Helmet>
      <SchemaOrg
        industryType={salon.industry_type}
        name={salon.name}
        url={`https://www.mestly.app/book/${salon.salon_code}`}
        telephone={salon.phone}
        address={salon.address}
        imageUrl={salon.logo_url ?? salon.cover_photo_url}
        openingHours={salon.opening_hours}
      />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Lato:wght@300;400;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');
        @keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }
        @keyframes pulse { 0%,100% { opacity:1; transform:scale(1) } 50% { opacity:0.5; transform:scale(1.4) } }
        .pb-scrollbar-hide::-webkit-scrollbar { display:none }
        .pb-scrollbar-hide { scrollbar-width:none; -ms-overflow-style:none }
        .pb-pro-card:hover .pb-pro-overlay { opacity: 1 !important; }

        /* ── Theme-aware interactive classes ── */
        .pb-svc-tab-btn {
          border: 1px solid var(--pb-tab-inactive-border);
          background: var(--pb-tab-inactive-bg);
          color: var(--pb-tab-inactive-color);
          border-radius: var(--pb-tab-radius);
        }
        .pb-svc-tab-btn.pb-tab-active {
          border-color: var(--pb-tab-active-border) !important;
          background: var(--pb-tab-active-bg) !important;
          color: var(--pb-tab-active-color) !important;
        }
        .pb-svc-btn {
          width: 32px; height: 32px; flex-shrink: 0;
          border-width: 1.5px; border-style: solid;
          border-radius: var(--pb-svc-btn-radius);
          background: var(--pb-svc-btn-bg);
          border-color: var(--pb-svc-btn-border);
          color: var(--pb-svc-btn-color);
          padding: 0; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s;
        }
        .pb-svc-btn.pb-svc-btn-sel {
          background: var(--pb-svc-btn-sel-bg) !important;
          border-color: var(--pb-svc-btn-sel-border) !important;
          color: var(--pb-svc-btn-sel-color) !important;
        }
        .pb-svc-btn:disabled { cursor: not-allowed; opacity: 0.4; }

        /* ── Responsive layout ── */
        .pb-inner { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
        .pb-hero-content { max-width: 680px; }
        .pb-team-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .pb-svc-layout { display: block; }
        .pb-svc-tabs { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 16px; scrollbar-width: none; }
        .pb-svc-tabs::-webkit-scrollbar { display: none; }
        .pb-svc-rows { display: block; }
        .pb-gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3px; }
        .pb-sticky-bar { max-width: 430px; left: 50%; transform: translateX(-50%); }
        .pb-booking-footer button,
        .pb-sticky-bar button {
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        @media (min-width: 768px) {
          .pb-inner { padding: 0 48px; }
          .pb-hero-inner { height: 560px !important; }
          .pb-infobar { padding: 16px 48px !important; }
          .pb-team-grid { grid-template-columns: repeat(5, 1fr); gap: 24px; }
          .pb-svc-layout { display: grid; grid-template-columns: 220px 1fr; gap: 40px; align-items: start; }
          .pb-svc-tabs { flex-direction: column; overflow-x: visible; padding-bottom: 0; gap: 4px; position: sticky; top: 24px; }
          .pb-svc-tab-btn { text-align: left !important; white-space: normal !important; }
          .pb-gallery-grid { grid-template-columns: repeat(4, 1fr); gap: 4px; }
          .pb-sticky-bar { max-width: 600px; }
        }

        @media (min-width: 1100px) {
          .pb-team-grid { grid-template-columns: repeat(6, 1fr); }
          .pb-gallery-grid { grid-template-columns: repeat(5, 1fr); gap: 5px; }
        }

        @media (max-width: 767px) {
          .pb-btn-label { display: none !important; }
          .pb-sticky-bar {
            background: var(--pb-sticky-bg) !important;
            left: 12px !important;
            right: 12px !important;
            bottom: max(10px, env(safe-area-inset-bottom)) !important;
            width: auto !important;
            max-width: none !important;
            transform: none !important;
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 12px;
            align-items: center;
            padding: 10px 12px !important;
            border: 1px solid rgba(255,255,255,0.55);
            border-radius: 18px;
            box-shadow: 0 18px 42px rgba(0,0,0,0.24) !important;
          }
          .pb-sticky-bar button {
            min-height: 44px;
            padding: 0 18px !important;
            white-space: nowrap;
          }
          .pb-sticky-bar p {
            overflow-wrap: anywhere;
          }
          .pb-modal-float {
            max-height: calc(100svh - max(10px, env(safe-area-inset-bottom))) !important;
            border-radius: 22px 22px 0 0 !important;
          }
          .pb-booking-scroll {
            padding-bottom: calc(150px + env(safe-area-inset-bottom)) !important;
          }
          .pb-booking-footer {
            background: var(--pb-modal-card) !important;
            padding: 10px 16px calc(14px + env(safe-area-inset-bottom)) !important;
            box-shadow: 0 -18px 38px rgba(15,23,42,0.09);
          }
          .pb-booking-footer-row {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 12px;
            align-items: center !important;
          }
          .pb-booking-footer-actions {
            display: grid !important;
            grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.4fr);
            gap: 10px !important;
          }
          .pb-booking-footer button {
            min-height: 44px;
            white-space: nowrap;
          }
        }

        @media (max-width: 390px) {
          .pb-booking-footer-row {
            grid-template-columns: 1fr;
          }
          .pb-booking-footer-row button {
            width: 100%;
          }
          .pb-sticky-bar {
            grid-template-columns: 1fr;
          }
          .pb-sticky-bar button {
            width: 100%;
          }
        }

        /* ── Desktop sidebar layout ── */
        .pb-page-wrapper { display: block; }
        .pb-cart-sidebar { display: none; }
        @media (min-width: 1024px) {
          .pb-page-wrapper { display: flex; align-items: flex-start; }
          .pb-page-body { flex: 1; min-width: 0; }
          .pb-cart-sidebar { display: block; width: 320px; flex-shrink: 0; border-left: 1px solid var(--pb-border); position: sticky; top: 0; align-self: flex-start; min-height: 100vh; }
          .pb-cart-sticky { padding: 32px 20px; }
          .pb-sticky-bar { display: none !important; }
        }

        /* ── Modal float (desktop center) ── */
        @media (min-width: 768px) {
          .pb-modal-float {
            inset: auto !important;
            top: 50% !important;
            left: 50% !important;
            right: auto !important;
            bottom: auto !important;
            transform: translate(-50%, -50%) !important;
            max-width: min(560px, 95vw) !important;
            width: min(560px, 95vw) !important;
            max-height: 88vh !important;
            border-radius: 20px !important;
          }
        }
      `}</style>

      <div style={{ fontFamily: 'var(--pb-font-body)', background: 'var(--pb-bg)', minHeight: '100svh', ...themeVars } as React.CSSProperties}>

        {/* ── HERO ──────────────────────────────────────────────────── */}
        <div className="pb-hero-inner" style={{ position: 'relative', height: 420, overflow: 'hidden', background: 'var(--pb-hero-bg-fallback)' }}>
          {salon.cover_photo_url
            ? <img src={salon.cover_photo_url} alt={salon.name} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'var(--pb-hero-img-filter)', opacity: heroImgOpacity }} />
            : <div style={{ width: '100%', height: '100%', background: 'var(--pb-hero-bg-fallback)' }} />
          }
          <div style={{ position: 'absolute', inset: 0, background: 'var(--pb-hero-overlay)' }} />
          <div className="pb-inner" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: 40 }}>
            <div className="pb-hero-content">
              {salon.logo_url && (
                <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(255,255,255,0.35)', marginBottom: 16, flexShrink: 0 }}>
                  <img src={salon.logo_url} alt={salon.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              <h1 style={{ fontFamily: 'var(--pb-font-heading)', color: 'white', fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 800, lineHeight: 1.1, margin: '0 0 8px', letterSpacing: '-0.02em' }}>{salon.name}</h1>
              {salon.address && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 28 }}>
                  <MapPin style={{ width: 12, height: 12, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, margin: 0 }}>{salon.address}</p>
                </div>
              )}
              <button
                onClick={() => openSheet()}
                style={{ alignSelf: 'flex-start', background: 'var(--pb-hero-btn-bg)', color: 'var(--pb-hero-btn-color)', border: 'none', padding: '12px 32px', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 'var(--pb-hero-btn-radius)' }}
              >
                Reservar cita
              </button>
            </div>
          </div>
        </div>

        {/* ── INFO BAR ──────────────────────────────────────────────── */}
          <div className="pb-infobar" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 24px', borderBottom: '1px solid var(--pb-border)', background: 'var(--pb-infobar-bg)' }}>
            <div className="pb-inner" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', width: '100%', padding: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--pb-dot-color)', animation: 'pulse 2.5s infinite', flexShrink: 0 }} />
                <p style={{ fontSize: 11, color: 'var(--pb-text-muted)', margin: 0 }}>Disponibilidad en tiempo real</p>
              </div>
              <div style={{ flex: 1 }} />
              {(whatsappUrl || socialButtons.length > 0 || salon.address) && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                  {socialButtons.map(({ key, href, label, icon: Icon }) => (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      title={label}
                      style={{
                        width: 34, height: 34,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)',
                        color: 'var(--pb-phone-color)', background: 'rgba(255,255,255,0.9)',
                        textDecoration: 'none', flexShrink: 0,
                      }}
                    >
                      <Icon style={{ width: 15, height: 15 }} />
                    </a>
                  ))}
                  {Object.keys(salon.opening_hours ?? {}).length > 0 && (() => {
                    const DAYS = [
                      { key: 'monday', label: 'Lunes' }, { key: 'tuesday', label: 'Martes' },
                      { key: 'wednesday', label: 'Miércoles' }, { key: 'thursday', label: 'Jueves' },
                      { key: 'friday', label: 'Viernes' }, { key: 'saturday', label: 'Sábado' },
                      { key: 'sunday', label: 'Domingo' },
                    ];
                    const todayKey = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date().getDay()];
                    return (
                      <div ref={hoursRef} style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          onClick={() => setShowHours(v => !v)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '9px 14px', borderRadius: 999,
                            border: '1px solid rgba(0,0,0,0.08)',
                            background: showHours ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.92)',
                            color: 'var(--pb-phone-color)',
                            fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
                            cursor: 'pointer',
                          }}
                        >
                          <Clock style={{ width: 15, height: 15 }} />
                          <span className="pb-btn-label">Horarios</span>
                        </button>

                        {showHours && (
                          <div style={{
                            position: 'absolute', bottom: 'calc(100% + 8px)', right: 0,
                            background: '#fff', borderRadius: 14,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                            border: '1px solid rgba(0,0,0,0.07)',
                            minWidth: 220, padding: '12px 0', zIndex: 9999,
                          }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--pb-text-muted)', padding: '0 16px 8px', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                              Horarios de atención
                            </p>
                            {DAYS.map(({ key, label }) => {
                              const entry = salon.opening_hours?.[key];
                              const isOpen = entry?.open != null;
                              const isToday = key === todayKey;
                              return (
                                <div key={key} style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '6px 16px',
                                  background: isToday ? 'rgba(22,85,104,0.06)' : 'transparent',
                                }}>
                                  <span style={{ fontSize: 13, fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--pb-primary)' : 'var(--pb-text)' }}>
                                    {label}{isToday && <span style={{ fontSize: 10, marginLeft: 5, opacity: 0.7 }}>● hoy</span>}
                                  </span>
                                  <span style={{ fontSize: 12, color: isOpen ? 'var(--pb-text)' : 'var(--pb-text-muted)', fontWeight: isToday ? 700 : 400 }}>
                                    {isOpen ? `${entry!.open} – ${entry!.close}` : 'Cerrado'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {(salon.address || salon.settings?.google_maps_url) && (
                    <a
                      href={
                        (salon.settings?.google_maps_url as string | undefined)?.trim() ||
                        (mapCoords
                          ? `https://www.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}`
                          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(salon.address ?? '')}`)
                      }
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '9px 14px', borderRadius: 999,
                        border: '1px solid rgba(0,0,0,0.08)',
                        background: 'rgba(255,255,255,0.92)',
                        color: 'var(--pb-phone-color)',
                        textDecoration: 'none', fontSize: 12, fontWeight: 700,
                        letterSpacing: '0.02em', flexShrink: 0,
                      }}
                    >
                      <MapPin style={{ width: 15, height: 15 }} />
                      <span className="pb-btn-label">Ubicación</span>
                    </a>
                  )}
                  {whatsappUrl && (
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '9px 14px', borderRadius: 999,
                        border: '1px solid rgba(0,0,0,0.08)',
                        background: 'rgba(255,255,255,0.92)',
                        color: 'var(--pb-phone-color)',
                        textDecoration: 'none', fontSize: 12, fontWeight: 700,
                        letterSpacing: '0.02em', flexShrink: 0,
                      }}
                    >
                      {/* WhatsApp official icon */}
                      <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, fill: 'currentColor' }} xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span className="pb-btn-label">Contactar por WhatsApp</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

        {/* ── TEAM ──────────────────────────────────────────────────── */}
        {professionals.length > 0 && (
          <section style={{ borderBottom: '1px solid var(--pb-border)', padding: '48px 0' }}>
            <div className="pb-inner">
              <h2 style={{ fontFamily: 'var(--pb-font-heading)', fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 700, color: 'var(--pb-text)', margin: '0 0 28px', letterSpacing: '-0.01em' }}>
                Nuestro equipo
              </h2>
              <div className="pb-team-grid">
                {professionals.map((pro) => {
                  const roleLabel = pro.job_title?.trim() || 'Profesional';

                  return (
                    <div key={pro.id} className="pb-pro-card" style={{ textAlign: 'center', cursor: 'default' }}>

                      {/* Avatar + botón PERFIL */}
                      <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 12px' }}>
                        <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', border: '1px solid var(--pb-pro-photo-border)', background: 'var(--pb-pro-initials-bg)' }}>
                          {pro.avatar_url
                            ? <img src={pro.avatar_url} alt={pro.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--pb-pro-initials-color)' }}>{getInitials(pro.full_name)}</span>
                              </div>
                          }
                        </div>
                        {/* Botón PERFIL — pill flotante abajo a la derecha del avatar */}
                        <button
                          onClick={() => setProfilePro(pro)}
                          style={{
                            position: 'absolute', bottom: -4, right: -10,
                            padding: '3px 9px', borderRadius: 20,
                            background: 'var(--pb-bg)',
                            border: '1px solid var(--pb-border)',
                            color: 'var(--pb-text-muted)',
                            fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
                            textTransform: 'uppercase', cursor: 'pointer',
                            lineHeight: 1.5, whiteSpace: 'nowrap',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                            transition: 'border-color 0.15s, color 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--pb-text-muted)'; e.currentTarget.style.color = 'var(--pb-text)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--pb-border)'; e.currentTarget.style.color = 'var(--pb-text-muted)'; }}
                        >
                          Perfil
                        </button>
                      </div>

                      {/* Nombre */}
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-text)', margin: 0, lineHeight: 1.3 }}>
                        {pro.full_name.split(' ')[0]}
                      </p>

                      {/* Rol */}
                      <p style={{ fontSize: 11, color: 'var(--pb-text-muted)', margin: '3px 0 0', fontStyle: 'italic' }}>
                        {roleLabel}
                      </p>

                      {/* Rating con estrellas */}
                      {pro.average_rating && pro.total_reviews ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, margin: '6px 0 0' }}>
                          {[1,2,3,4,5].map((i) => (
                            <span key={i} style={{ fontSize: 12, lineHeight: 1, color: i <= Math.round(pro.average_rating!) ? '#f59e0b' : 'var(--pb-border)' }}>★</span>
                          ))}
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pb-text)', marginLeft: 2 }}>{pro.average_rating.toFixed(1)}</span>
                          <span style={{ fontSize: 10, color: 'var(--pb-text-muted)' }}>({pro.total_reviews})</span>
                        </div>
                      ) : null}

                      {/* CTA Reservar */}
                      <button
                        onClick={() => openSheet(false, pro.id)}
                        style={{
                          display: 'block', width: '100%', marginTop: 14,
                          padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                          fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          background: 'transparent',
                          border: '1.5px solid #9b8a78',
                          color: 'var(--pb-text)',
                          transition: 'background 0.15s, color 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#9b8a78'; e.currentTarget.style.color = '#ffffff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--pb-text)'; }}
                      >
                        Reservar
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <div className="pb-page-wrapper">
        <div className="pb-page-body">
        {/* ── SERVICES ──────────────────────────────────────────────── */}
        <section style={{ borderBottom: '1px solid var(--pb-border)', padding: '48px 0' }}>
          <div className="pb-inner">
            <h2 style={{ fontFamily: 'var(--pb-font-heading)', fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 700, color: 'var(--pb-text)', margin: '0 0 28px', letterSpacing: '-0.01em' }}>
              Servicios
            </h2>
            <div className="pb-svc-layout">
              {/* Category tabs — col on desktop, row on mobile */}
              <div className="pb-svc-tabs">
                {orderedCategoryEntries.map(([cat]) => cat).map((tab) => (
                  <button
                    key={tab}
                    className={`pb-svc-tab-btn${landingCatTab === tab ? ' pb-tab-active' : ''}`}
                    onClick={() => setLandingCatTab(tab)}
                    style={{
                      flexShrink: 0, padding: '7px 16px',
                      fontSize: 11, fontWeight: 600, letterSpacing: '0.05em',
                      textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap',
                      transition: 'all 0.15s', textAlign: 'center',
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Service rows */}
              <div className="pb-svc-rows">
                {orderedCategoryEntries
                  .filter(([cat]) => !landingCatTab || cat === landingCatTab)
                  .map(([, catSvcs]) => catSvcs)
                  .flat()
                  .map((svc) => {
                    const isSel = selectedSvcs.some((s) => s.serviceId === svc.id);
                    const isMaxCats = selectedCategories.size >= 3 && !selectedCategories.has(svc.category);
                    return (
                      <div key={svc.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderBottom: '1px solid var(--pb-svc-border)', opacity: isMaxCats ? 0.4 : 1 }}>
                        {svc.image_url && (
                          <div style={{ width: 56, height: 56, borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
                            <img src={svc.image_url} alt={svc.name} className="pb-svc-img" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--pb-svc-name-color)', margin: '0 0 3px' }}>{svc.name}</p>
                          {svc.description && (() => {
                            const LIMIT = 80;
                            const isLong = svc.description.length > LIMIT;
                            const isExpanded = expandedDescs.has(svc.id);
                            const text = isLong && !isExpanded
                              ? svc.description.slice(0, LIMIT).trimEnd() + '…'
                              : svc.description;
                            return (
                              <div style={{ margin: '0 0 6px' }}>
                                <span style={{ fontSize: 12, color: 'var(--pb-svc-meta-color)', lineHeight: 1.25, opacity: 0.8 }}>
                                  {text}
                                </span>
                                {isLong && (
                                  <button
                                    onClick={e => { e.stopPropagation(); toggleDesc(svc.id); }}
                                    style={{
                                      background: 'none', border: 'none', padding: '0 0 0 4px',
                                      fontSize: 11, fontWeight: 700, color: 'var(--pb-primary)',
                                      cursor: 'pointer', opacity: 0.85,
                                    }}
                                  >
                                    {isExpanded ? 'Ver menos' : 'Ver más'}
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 11, fontWeight: 600, color: 'var(--pb-svc-meta-color)',
                              background: 'var(--pb-svc-border)', borderRadius: 20,
                              padding: '3px 9px',
                            }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                              {formatDur(svc.duration_minutes)}
                            </span>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center',
                              fontSize: 14, fontWeight: 800, color: 'var(--pb-primary)',
                              letterSpacing: '-0.3px',
                            }}>
                              {formatCurrency(svc.price, orgLocale)}
                            </span>
                          </div>
                        </div>
                        <button
                          disabled={isMaxCats}
                          onClick={() => !isMaxCats && toggleService(svc)}
                          className={`pb-svc-btn${isSel ? ' pb-svc-btn-sel' : ''}`}
                        >
                          {isSel ? <X style={{ width: 14, height: 14 }} /> : <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>}
                        </button>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        </section>

        {/* ── GALLERY ───────────────────────────────────────────────── */}
        {(salon.gallery_urls?.length ?? 0) > 0 && (
          <section style={{ borderBottom: '1px solid var(--pb-border)', padding: '48px 0' }}>
            <div className="pb-inner">
              <h2 style={{ fontFamily: 'var(--pb-font-heading)', fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 700, color: 'var(--pb-text)', margin: '0 0 20px', letterSpacing: '-0.01em' }}>
                Galería
              </h2>
              <div className="pb-gallery-grid">
                {salon.gallery_urls!.map((url, i) => (
                  <div key={i} onClick={() => setLightboxUrl(url)} style={{ aspectRatio: '1', overflow: 'hidden', cursor: 'pointer', background: 'var(--pb-pro-initials-bg)' }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'var(--pb-gallery-filter)', transition: 'filter 0.3s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.filter = 'var(--pb-gallery-filter-hover)')}
                      onMouseLeave={(e) => (e.currentTarget.style.filter = 'var(--pb-gallery-filter)')}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Bottom padding for sticky bar */}
        <div style={{ height: selectedSvcs.length > 0 ? 88 : 60 }} />

        </div>{/* end pb-page-body */}

        <aside className="pb-cart-sidebar">
          <div className="pb-cart-sticky">
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 16px' }}>
              Tu selección
            </p>
            {selectedSvcs.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--pb-text-muted)', margin: 0 }}>Selecciona servicios para continuar</p>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                  {selectedSvcs.map((s) => {
                    const svc = serviceById[s.serviceId];
                    if (!svc) return null;
                    return (
                      <div key={s.serviceId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-text)', margin: 0 }}>{svc.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--pb-text-muted)', margin: '2px 0 0' }}>{formatDur(svc.duration_minutes)}</p>
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-text)', margin: '0 0 0 8px', flexShrink: 0 }}>{formatCurrency(svc.price, orgLocale)}</p>
                      </div>
                    );
                  })}
                </div>
                <div style={{ borderTop: '1px solid var(--pb-border)', paddingTop: 12, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--pb-text)', margin: 0 }}>Total</p>
                    <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--pb-text)', margin: 0 }}>{formatCurrency(totalPrice, orgLocale)}</p>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--pb-text-muted)', margin: '4px 0 0' }}>Duración aprox. {formatDur(totalDuration)}</p>
                </div>
                <button
                  onClick={() => openSheet(true)}
                  style={{ width: '100%', background: 'var(--pb-hero-btn-bg)', color: 'var(--pb-hero-btn-color)', border: 'none', padding: '12px 0', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 'var(--pb-hero-btn-radius)' }}
                >
                  Reservar →
                </button>
              </>
            )}
          </div>
        </aside>

        </div>{/* end pb-page-wrapper */}

      </div>

      {/* ── STICKY BOOKING BAR ────────────────────────────────────── */}
      {selectedSvcs.length > 0 && !sheetOpen && (
        <div className="pb-sticky-bar" style={{
          position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
          width: '100%', background: 'var(--pb-sticky-bg)',
          padding: '14px 28px 28px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', zIndex: 200,
          boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
        }}>
          <div>
            <p style={{ color: 'var(--pb-sticky-count-color)', fontSize: 10, margin: '0 0 2px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {selectedSvcs.length} servicio{selectedSvcs.length !== 1 ? 's' : ''}
            </p>
            <p style={{ color: 'var(--pb-sticky-price-color)', fontSize: 17, fontWeight: 700, margin: 0 }}>{formatCurrency(totalPrice, orgLocale)}</p>
          </div>
          <button
            onClick={() => openSheet(true)}
            style={{
              background: 'var(--pb-sticky-btn-bg)', color: 'var(--pb-sticky-btn-color)', border: 'none',
              padding: '10px 22px', fontSize: 12, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
              borderRadius: 'var(--pb-sticky-btn-radius)',
            }}
          >
            Reservar →
          </button>
        </div>
      )}

      {/* ── LIGHTBOX ──────────────────────────────────────────────── */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <img src={lightboxUrl} alt="" style={{ maxWidth: '92%', maxHeight: '88vh', objectFit: 'contain' }} />
          <button style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: '1px solid rgba(255,255,255,0.4)', color: 'white', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
      )}

      {/* ── BOTTOM SHEET ──────────────────────────────────────────── */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="bottom"
          className="[&>button]:hidden p-0 border-0 pb-modal-float"
          style={{ fontFamily: 'var(--pb-font-body)', background: 'var(--pb-modal-bg)', color: 'var(--pb-modal-text)', borderRadius: '24px 24px 0 0', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.18)', ...themeVars } as React.CSSProperties}
        >
          {/* Handle */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 6px', flexShrink: 0 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--pb-modal-handle)' }} />
          </div>

          {/* Header */}
          {!confirmed && (
            <div style={{ padding: '0 20px 12px', borderBottom: '1px solid var(--pb-modal-border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>Paso {step} de 5</p>
                  <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--pb-modal-text)', margin: '3px 0 0' }}>
                    {step === 1 && 'Elige tus servicios'}
                    {step === 2 && 'Cuéntanos quién eres'}
                    {step === 3 && 'Elige tu especialista'}
                    {step === 4 && 'Fecha y hora'}
                    {step === 5 && 'Resumen de tu cita'}
                  </p>
                </div>
                <button onClick={() => setSheetOpen(false)} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--pb-modal-card)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X style={{ width: 15, height: 15, color: 'var(--pb-modal-muted)' }} />
                </button>
              </div>
              {/* Progress */}
              <div style={{ display: 'flex', gap: 3 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? 'var(--pb-modal-accent)' : 'var(--pb-modal-border)', transition: 'background 0.3s' }} />
                ))}
              </div>
            </div>
          )}

          {/* Scrollable content */}
          <div className="pb-scrollbar-hide pb-booking-scroll" style={{ flex: 1, overflowY: 'auto', paddingBottom: confirmed ? 0 : 100 }}>

            {/* ── CONFIRMED ── */}
            {confirmed && (
              <div style={{ padding: '24px 20px 40px', textAlign: 'center' }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#1D9E75', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Check style={{ width: 36, height: 36, color: 'white' }} />
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--pb-modal-text)', margin: '0 0 6px' }}>¡Reserva confirmada!</h2>
                <p style={{ fontSize: 13, color: 'var(--pb-modal-muted)', margin: '0 0 20px' }}>Te enviaremos un recordatorio por WhatsApp</p>

                {/* Service list */}
                <div style={{ background: 'var(--pb-modal-card)', borderRadius: 16, padding: 16, border: '1px solid #e8e5de', textAlign: 'left', marginBottom: 12 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, marginTop: 0 }}>Resumen</p>
                  {selectedSvcs.map((s) => {
                    const svc = serviceById[s.serviceId];
                    if (!svc) return null;
                    return (
                      <div key={s.serviceId} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{svc.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: '2px 0 0' }}>{formatDur(svc.duration_minutes)} · {svc.category}</p>
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{formatCurrency(svc.price, orgLocale)}</p>
                      </div>
                    );
                  })}
                  <div style={{ borderTop: '1px solid var(--pb-modal-border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <p style={{ fontWeight: 700, color: 'var(--pb-modal-text)', fontSize: 14, margin: 0 }}>Total</p>
                    <p style={{ fontWeight: 700, color: 'var(--pb-modal-text)', fontSize: 14, margin: 0 }}>{formatCurrency(totalPrice, orgLocale)}</p>
                  </div>
                </div>

                {/* Details grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  {confirmedPro && (
                    <div style={{ background: 'var(--pb-modal-card)', borderRadius: 14, padding: 12, border: '1px solid #e8e5de', textAlign: 'left' }}>
                      <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, marginTop: 0 }}>Especialista</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: confirmedPro.color ?? 'var(--pb-modal-accent)', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {confirmedPro.avatar_url ? <img src={confirmedPro.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: 'white', fontSize: 10, fontWeight: 700 }}>{getInitials(confirmedPro.full_name)}</span>}
                        </div>
                        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{confirmedPro.full_name}</p>
                      </div>
                    </div>
                  )}
                  {selectedDate && selectedSlot !== null && (
                    <div style={{ background: 'var(--pb-modal-card)', borderRadius: 14, padding: 12, border: '1px solid #e8e5de', textAlign: 'left' }}>
                      <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4, marginTop: 0 }}>Fecha y hora</p>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{format(selectedDate, "d 'de' MMMM", { locale: es })}</p>
                      <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--pb-modal-text)', margin: '2px 0 0' }}>{minutesToTime(selectedSlot)}</p>
                    </div>
                  )}
                </div>

                {effectiveDeposit > 0 && (
                  <div style={{ background: depositPaidConfirmed ? '#534AB7' : '#6B6459', borderRadius: 16, padding: 16, display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                      {/* Nunca se afirma un pago que no ocurrió: antes bastaba
                          con que hubiera un anticipo configurado para mostrar
                          "Abono pagado", aunque deposit_paid fuera false. */}
                      <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, margin: '0 0 2px' }}>{depositBadgeLabel(depositPaidConfirmed)}</p>
                      <p style={{ color: 'white', fontSize: 20, fontWeight: 700, margin: 0 }}>{formatCurrency(effectiveDeposit, orgLocale)}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, margin: '0 0 2px' }}>Resto al llegar</p>
                      <p style={{ color: 'white', fontSize: 20, fontWeight: 700, margin: 0 }}>{formatCurrency(totalPrice - effectiveDeposit, orgLocale)}</p>
                    </div>
                  </div>
                )}

                <button onClick={() => setSheetOpen(false)} style={{ width: '100%', background: 'var(--pb-modal-accent)', color: 'white', borderRadius: 14, padding: '13px 0', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                  Cerrar
                </button>
              </div>
            )}

            {/* ── STEP 1: SERVICES ── */}
            {!confirmed && step === 1 && (
              <div style={{ padding: '16px 20px 0' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {bookingCategoryEntries.map(([cat, catServices]) => {
                    const isOpen = openCategory === cat;
                    const selectedInCat = selectedSvcs.find((s) => s.category === cat);
                    const isMaxCats = selectedCategories.size >= 3 && !selectedCategories.has(cat);

                    return (
                      <div key={cat} style={{ background: 'var(--pb-modal-card)', borderRadius: 14, border: `1.5px solid ${selectedInCat ? 'var(--pb-modal-accent)' : 'var(--pb-modal-border)'}`, overflow: 'hidden', opacity: isMaxCats ? 0.5 : 1 }}>
                        <button
                          onClick={() => !isMaxCats && setOpenCategory(isOpen ? null : cat)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 14px', background: 'transparent', border: 'none', cursor: isMaxCats ? 'not-allowed' : 'pointer', textAlign: 'left' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Scissors style={{ width: 15, height: 15, color: 'var(--pb-modal-text)', flexShrink: 0 }} />
                            <div>
                              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{cat}</p>
                              {selectedInCat ? (
                                <p style={{ fontSize: 11, color: 'var(--pb-modal-text)', fontWeight: 500, margin: '2px 0 0', display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <Check style={{ width: 10, height: 10 }} />
                                  {serviceById[selectedInCat.serviceId]?.name}
                                </p>
                              ) : (
                                <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: '2px 0 0' }}>{catServices.length} servicio{catServices.length !== 1 ? 's' : ''}</p>
                              )}
                            </div>
                          </div>
                          <ChevronDown style={{ width: 15, height: 15, color: 'var(--pb-modal-muted)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                        </button>

                        <div style={{ maxHeight: isOpen ? `${catServices.length * 64}px` : 0, overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                          {catServices.map((svc, idx) => {
                            const isSel = selectedSvcs.some((s) => s.serviceId === svc.id);
                            return (
                              <div key={svc.id}>
                                {idx > 0 && <div style={{ height: 1, background: 'var(--pb-modal-card)', margin: '0 14px' }} />}
                                        <button
                                  onClick={() => toggleService(svc)}
                                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', padding: '10px 14px', background: isSel ? 'rgba(0,0,0,0.04)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                >
                                  {svc.image_url && (
                                    <div style={{ width: 44, height: 44, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--pb-modal-card)' }}>
                                      <img src={svc.image_url} alt={svc.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{svc.name}</p>
                                    <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: '2px 0 0' }}>{formatDur(svc.duration_minutes)} · {formatCurrency(svc.price, orgLocale)}</p>
                                  </div>
                                  <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${isSel ? 'var(--pb-modal-accent)' : 'rgba(0,0,0,0.18)'}`, background: isSel ? 'var(--pb-modal-accent)' : 'rgba(255,255,255,0.7)', boxShadow: isSel ? '0 0 0 2px rgba(255,255,255,0.8)' : 'inset 0 0 0 1px rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {isSel && <Check style={{ width: 11, height: 11, color: 'white', strokeWidth: 3 }} />}
                                  </div>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── STEP 2: CLIENT IDENTIFICATION (V3) ── */}
            {!confirmed && step === 2 && (
              <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Returning client greeting */}
                {clientHistory && (
                  <div style={{ background: '#f0fdf9', borderRadius: 12, padding: '10px 14px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 22 }}>👋</span>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#166534', margin: 0 }}>¡Hola de nuevo, {clientHistory.clientName.split(' ')[0]}!</p>
                      <p style={{ fontSize: 11, color: '#166534', margin: '1px 0 0', opacity: 0.8 }}>{clientHistory.total_visits} visita{clientHistory.total_visits !== 1 ? 's' : ''} anteriores</p>
                    </div>
                  </div>
                )}
                {/* Phone first (triggers lookup) */}
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>Teléfono *</label>
                  <div style={{ position: 'relative' }}>
                    <Input
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder={getPhonePlaceholder(orgLocale)}
                      style={{ borderRadius: 12, border: '1.5px solid #e8e5de', background: 'var(--pb-modal-card)', fontSize: 16, height: 44, paddingRight: lookingUpClient ? 44 : undefined }}
                    />
                    {lookingUpClient && (
                      <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                        <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #e8e5de', borderTopColor: 'var(--pb-modal-accent)', animation: 'spin 0.8s linear infinite' }} />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>Nombre completo *</label>
                  <Input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Tu nombre completo"
                    style={{ borderRadius: 12, border: '1.5px solid #e8e5de', background: 'var(--pb-modal-card)', fontSize: 16, height: 44 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>Email (opcional)</label>
                  <Input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="tu@email.com"
                    style={{ borderRadius: 12, border: '1.5px solid #e8e5de', background: 'var(--pb-modal-card)', fontSize: 16, height: 44 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>Código de descuento (opcional)</label>
                  <Input
                    type="text"
                    value={campaignCode}
                    onChange={(e) => setCampaignCode(e.target.value)}
                    placeholder="Ej: VERANO20"
                    style={{ borderRadius: 12, border: '1.5px solid #e8e5de', background: 'var(--pb-modal-card)', fontSize: 16, height: 44, textTransform: 'uppercase' }}
                  />
                  {campaignCode.trim() && (
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      {checkingCampaignCode ? (
                        <>
                          <Loader2 style={{ width: 13, height: 13, color: 'var(--pb-modal-muted)', animation: 'spin 1s linear infinite' }} />
                          <span style={{ color: 'var(--pb-modal-muted)' }}>Verificando código...</span>
                        </>
                      ) : campaignCodeCheck?.valid ? (
                        <>
                          <Check style={{ width: 13, height: 13, color: '#16a34a' }} />
                          <span style={{ color: '#16a34a', fontWeight: 600 }}>
                            Código válido
                            {campaignCodeCheck.discount_value
                              ? ` — ${campaignCodeCheck.discount_type === 'percentage'
                                ? `${campaignCodeCheck.discount_value}%`
                                : formatCurrency(campaignCodeCheck.discount_value, orgLocale)} de descuento`
                              : ''}
                          </span>
                        </>
                      ) : campaignCodeCheck && !campaignCodeCheck.valid ? (
                        <>
                          <X style={{ width: 13, height: 13, color: '#dc2626' }} />
                          <span style={{ color: '#dc2626', fontWeight: 600 }}>
                            {campaignCodeCheck.reason === 'expired' ? 'Este código venció' : 'Este código no existe'}
                          </span>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'var(--pb-modal-card)', borderRadius: 12, padding: '10px 14px', border: '1px solid #e8e5de' }}>
                  <Lock style={{ width: 13, height: 13, color: 'var(--pb-modal-muted)', flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', lineHeight: 1.5, margin: 0 }}>Tus datos son privados y solo se usan para gestionar tu reserva. No los compartimos.</p>
                </div>
              </div>
            )}

            {/* ── STEP 3: PROFESSIONAL ── */}
            {!confirmed && step === 3 && (
              <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* No preference */}
                <div
                  onClick={() => undefined}
                  style={{ display: 'none', background: 'white', borderRadius: 14, border: `2px solid var(--pb-modal-border)`, padding: '13px 14px', cursor: 'default', alignItems: 'center', gap: 12 }}
                >
                  <span style={{ fontSize: 26 }}>🎲</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>Profesional</p>
                    <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: '2px 0 0' }}>Selecciona una persona para continuar</p>
                  </div>
                  {NO_PREFERENCE_OPTION_ENABLED && <Check style={{ width: 16, height: 16, color: 'var(--pb-modal-text)', flexShrink: 0 }} />}
                </div>

                {eligiblePros.length === 0 ? (
                  <div style={{ background: '#fff8f0', borderRadius: 14, padding: '14px', border: '1px solid #ffe4c4', textAlign: 'center' }}>
                    <p style={{ fontSize: 13, color: 'var(--pb-modal-muted)', margin: 0 }}>Ningún especialista puede realizar todos los servicios seleccionados.</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {eligiblePros.map((pro) => {
                      const isSel = selectedProId === pro.id;
                      const isHabitual = clientHistory?.preferred_professional_id === pro.id;
                      return (
                        <div
                          key={pro.id}
                          onClick={() => {
                            if (reservationId) {
                              void releaseSlotReservation(reservationId);
                              setReservationId(null);
                              setReservationExpiresAt(null);
                            }
                            setSelectedProId(pro.id);
                            setSelectedDate(null);
                            setSelectedSlot(null);
                            setSlots([]);
                            setDayFull(false);
                            setBookingError(null);
                          }}
                          style={{ background: isSel ? 'rgba(0,0,0,0.04)' : 'white', borderRadius: 14, border: `2px solid ${isSel ? (pro.color ?? 'var(--pb-modal-accent)') : isHabitual ? '#2db1c3' : 'var(--pb-modal-border)'}`, padding: '14px 10px', cursor: 'pointer', textAlign: 'center', position: 'relative' }}
                        >
                          {isHabitual && (
                            <div style={{ position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)', background: '#2db1c3', color: 'white', fontSize: 8, fontWeight: 800, letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                              ⭐ TU HABITUAL
                            </div>
                          )}
                          <div style={{ width: 52, height: 52, borderRadius: '50%', background: pro.color ?? 'var(--pb-modal-accent)', margin: '0 auto 8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {pro.avatar_url ? <img src={pro.avatar_url} alt={pro.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>{getInitials(pro.full_name)}</span>}
                          </div>
                          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0, lineHeight: 1.3 }}>{pro.full_name}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 4: DATE & TIME ── */}
            {!confirmed && step === 4 && (
              <div style={{ padding: '16px 0 0' }}>
                {bookingError && (
                  <div style={{ margin: '0 20px 12px', background: '#fff0f0', borderRadius: 12, padding: '10px 14px', border: '1px solid #ffcccc' }}>
                    <p style={{ fontSize: 12, color: '#cc4444', margin: 0 }}>{bookingError}</p>
                  </div>
                )}

                {/* Date pills */}
                <div style={{ paddingLeft: 20, marginBottom: 20 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, marginTop: 0 }}>Selecciona un día</p>
                  <div className="pb-scrollbar-hide" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, paddingRight: 20 }}>
                    {Array.from({ length: 21 }, (_, i) => addDays(new Date(), i)).map((date) => {
                      const open = isDayOpen(date);
                      const isSel = selectedDate?.toDateString() === date.toDateString();
                      return (
                        <button
                          key={date.toISOString()}
                          disabled={!open}
                          onClick={() => void handleDateSelect(date)}
                          style={{ flexShrink: 0, width: 52, borderRadius: 13, padding: '9px 4px', background: isSel ? 'var(--pb-modal-accent)' : 'white', border: `1.5px solid ${isSel ? 'var(--pb-modal-accent)' : 'var(--pb-modal-border)'}`, cursor: open ? 'pointer' : 'not-allowed', opacity: open ? 1 : 0.35, textAlign: 'center', textDecoration: !open ? 'line-through' : 'none' }}
                        >
                          <p style={{ fontSize: 9, fontWeight: 700, color: isSel ? 'rgba(255,255,255,0.75)' : '#9c9890', textTransform: 'uppercase', margin: 0, letterSpacing: '0.04em' }}>{DAY_ABBR[date.getDay()]}</p>
                          <p style={{ fontSize: 18, fontWeight: 700, color: isSel ? 'white' : 'var(--pb-modal-accent)', margin: '1px 0' }}>{date.getDate()}</p>
                          <p style={{ fontSize: 9, color: isSel ? 'rgba(255,255,255,0.65)' : '#9c9890', margin: 0 }}>{format(date, 'MMM', { locale: es })}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Slots */}
                {selectedDate && (
                  <div style={{ padding: '0 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Horarios disponibles</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#e8faf3', borderRadius: 20, padding: '3px 8px' }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#1D9E75', animation: 'pulse 2s infinite' }} />
                        <p style={{ fontSize: 9, fontWeight: 700, color: '#1D9E75', margin: 0 }}>EN VIVO</p>
                      </div>
                    </div>

                    {loadingSlots ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '28px 0' }}>
                        <Loader2 style={{ width: 18, height: 18, color: 'var(--pb-modal-text)', animation: 'spin 1s linear infinite' }} />
                        <p style={{ fontSize: 13, color: 'var(--pb-modal-muted)', margin: 0 }}>Buscando disponibilidad...</p>
                      </div>
                    ) : slots.length === 0 ? (
                      (() => {
                        const rule = getIndustryRule(salon?.industry_type);
                        const showWaitlist = dayFull && rule.waitlist;
                        if (showWaitlist) {
                          return (
                            <div style={{ background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 14, padding: '16px 18px' }}>
                              {waitlistDone ? (
                                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                  <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                                  <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--pb-modal-text)', margin: '0 0 4px' }}>¡Listo! Estás en la lista</p>
                                  <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Te avisaremos si se libera un lugar para este día.</p>
                                </div>
                              ) : (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <span style={{ fontSize: 18 }}>🔔</span>
                                    <div>
                                      <p style={{ fontSize: 13, fontWeight: 700, color: '#92400e', margin: 0 }}>Agenda completa para este día</p>
                                      <p style={{ fontSize: 11, color: '#b45309', margin: '2px 0 0' }}>Anótate y te avisamos si se libera un turno.</p>
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <input
                                      value={waitlistNotes}
                                      onChange={(e) => setWaitlistNotes(e.target.value)}
                                      placeholder="Nota opcional (ej: prefiero mañana)"
                                      style={{ width: '100%', padding: '8px 12px', borderRadius: 10, border: '1.5px solid #f59e0b', fontSize: 16, background: 'var(--pb-modal-card)', outline: 'none', boxSizing: 'border-box' }}
                                    />
                                    <button
                                      onClick={() => void handleWaitlist()}
                                      disabled={submittingWaitlist || !clientName.trim() || !clientPhone.trim()}
                                      style={{ padding: '10px 0', borderRadius: 12, background: submittingWaitlist ? '#fde68a' : '#f59e0b', color: 'white', fontWeight: 700, fontSize: 13, border: 'none', cursor: submittingWaitlist ? 'not-allowed' : 'pointer', width: '100%' }}
                                    >
                                      {submittingWaitlist ? 'Anotando...' : '🔔 Anotarme en lista de espera'}
                                    </button>
                                    {(!clientName.trim() || !clientPhone.trim()) && (
                                      <p style={{ fontSize: 11, color: '#b45309', margin: 0, textAlign: 'center' }}>Completa tu nombre y teléfono en el paso anterior.</p>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        }
                        // "Sin disponibilidad" sería engañoso cuando el
                        // problema es que no pudimos consultar la hora: el día
                        // podría tener cupo de sobra.
                        if (slotsUnavailable) {
                          return (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                              <p style={{ fontSize: 13, color: 'var(--pb-modal-muted)', margin: '0 0 12px' }}>
                                No pudimos cargar los horarios disponibles. Intenta nuevamente.
                              </p>
                              <button
                                type="button"
                                onClick={() => { if (selectedDate) loadSlots(selectedDate, selectedProId); }}
                                style={{
                                  fontSize: 13, fontWeight: 600, padding: '8px 16px',
                                  borderRadius: 8, border: '1px solid var(--pb-modal-muted)',
                                  background: 'transparent', color: 'var(--pb-modal-muted)',
                                  cursor: 'pointer',
                                }}
                              >
                                Reintentar
                              </button>
                            </div>
                          );
                        }
                        return <p style={{ fontSize: 13, color: 'var(--pb-modal-muted)', textAlign: 'center', padding: '20px 0', margin: 0 }}>Sin disponibilidad para este día</p>;
                      })()
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {slots.some((s) => s.isRecommended) && (
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 600, color: '#0F6E56', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Star style={{ width: 11, height: 11 }} /> Recomendados
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {slots.filter((s) => s.isRecommended).map((slot) => (
                                <button key={slot.startMin} onClick={() => void handleSlotSelect(slot.startMin)} style={{ padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${selectedSlot === slot.startMin ? '#1D9E75' : slot.isDeadHour ? '#f59e0b' : '#2db1c3'}`, background: selectedSlot === slot.startMin ? '#1D9E75' : slot.isDeadHour ? '#fffbeb' : '#e8faf3', color: selectedSlot === slot.startMin ? 'white' : slot.isDeadHour ? '#92400e' : '#0F6E56', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                  {slot.isDeadHour && '⚡ '}{minutesToTime(slot.startMin)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {slots.some((s) => !s.isRecommended) && (
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--pb-modal-muted)', margin: '0 0 8px' }}>Otros disponibles</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {slots.filter((s) => !s.isRecommended).map((slot) => (
                                <button key={slot.startMin} onClick={() => void handleSlotSelect(slot.startMin)} style={{ padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${selectedSlot === slot.startMin ? 'var(--pb-modal-accent)' : slot.isDeadHour ? '#f59e0b' : 'var(--pb-modal-border)'}`, background: selectedSlot === slot.startMin ? 'var(--pb-modal-accent)' : slot.isDeadHour ? '#fffbeb' : 'white', color: selectedSlot === slot.startMin ? 'white' : slot.isDeadHour ? '#92400e' : 'var(--pb-modal-accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                  {slot.isDeadHour && '⚡ '}{minutesToTime(slot.startMin)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}


            {/* ── STEP 5: SUMMARY ── */}
            {!confirmed && step === 5 && (
              <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Services */}
                <div style={{ background: 'var(--pb-modal-card)', borderRadius: 14, padding: 14, border: '1px solid #e8e5de' }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>Servicios</p>
                  {selectedSvcs.map((s) => {
                    const svc = serviceById[s.serviceId];
                    if (!svc) return null;
                    return (
                      <div key={s.serviceId} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{svc.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: '2px 0 0' }}>{formatDur(svc.duration_minutes)} · {svc.category}</p>
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{formatCurrency(svc.price, orgLocale)}</p>
                      </div>
                    );
                  })}
                  <div style={{ borderTop: '1px solid var(--pb-modal-border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontWeight: 700, color: 'var(--pb-modal-text)', fontSize: 14, margin: 0 }}>Total</p>
                      <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: '2px 0 0' }}>Duración aprox. {formatDur(totalDuration)}</p>
                    </div>
                    <p style={{ fontWeight: 700, color: 'var(--pb-modal-text)', fontSize: 14, margin: 0 }}>{formatCurrency(totalPrice, orgLocale)}</p>
                  </div>
                </div>

                {/* Details grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ background: 'var(--pb-modal-card)', borderRadius: 13, padding: 12, border: '1px solid #e8e5de' }}>
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Especialista</p>
                    {NO_PREFERENCE_OPTION_ENABLED ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 22 }}>🎲</span>
                        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>Profesional</p>
                      </div>
                    ) : (() => {
                      const pro = professionals.find((p) => p.id === selectedProId);
                      if (!pro) return null;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: pro.color ?? 'var(--pb-modal-accent)', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {pro.avatar_url ? <img src={pro.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: 'white', fontSize: 10, fontWeight: 700 }}>{getInitials(pro.full_name)}</span>}
                          </div>
                          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0, lineHeight: 1.2 }}>{pro.full_name}</p>
                        </div>
                      );
                    })()}
                  </div>
                  {selectedDate && selectedSlot !== null && (
                    <div style={{ background: 'var(--pb-modal-card)', borderRadius: 13, padding: 12, border: '1px solid #e8e5de' }}>
                      <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Fecha y hora</p>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--pb-modal-text)', margin: 0 }}>{format(selectedDate, "d MMM", { locale: es })}</p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--pb-modal-text)', margin: '2px 0 0' }}>{minutesToTime(selectedSlot)}</p>
                    </div>
                  )}
                </div>

                {/* Fase 4: no-show warning */}
                {clientHistory && clientHistory.no_show_count >= 2 && (
                  <div style={{ background: '#fff7ed', borderRadius: 12, padding: '10px 14px', border: '1px solid #fed7aa', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
                    <p style={{ fontSize: 12, color: '#92400e', margin: 0, lineHeight: 1.5 }}>
                      Tienes {clientHistory.no_show_count} inasistencias previas. Se requiere un abono mínimo del 20% para confirmar tu reserva.
                    </p>
                  </div>
                )}

                {/* Fase 4: reservation countdown */}
                {reservationCountdown && (
                  <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '10px 14px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <p style={{ fontSize: 12, color: '#166534', margin: 0 }}>🔒 Horario reservado por</p>
                    <p style={{ fontSize: 14, fontWeight: 700, color: '#166534', margin: 0 }}>{reservationCountdown}</p>
                  </div>
                )}

                {/* Deposit box -- solo si el anticipo se va a cobrar de verdad.
                    Antes se derivaba de `depositAmount`, así que un negocio con
                    deposit_value configurado pero require_deposit=false mostraba
                    "Abono ahora $100 / Resto $X" sobre una reserva que no cobra
                    nada. La fuente de verdad es effectiveDeposit. */}
                {effectiveDeposit > 0 && (
                  <div style={{ background: '#534AB7', borderRadius: 16, padding: 16 }}>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>Pago de reserva</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, margin: '0 0 2px' }}>Abono ahora</p>
                        <p style={{ color: 'white', fontSize: 20, fontWeight: 700, margin: 0 }}>{formatCurrency(effectiveDeposit, orgLocale)}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, margin: '0 0 2px' }}>Resto al llegar</p>
                        <p style={{ color: 'white', fontSize: 20, fontWeight: 700, margin: 0 }}>{formatCurrency(totalPrice - effectiveDeposit, orgLocale)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {bookingError && (
                  <div style={{ background: '#fff0f0', borderRadius: 12, padding: '10px 14px', border: '1px solid #ffcccc' }}>
                    <p style={{ fontSize: 12, color: '#cc4444', margin: 0 }}>{bookingError}</p>
                  </div>
                )}
              </div>
            )}

          </div>{/* end scrollable */}

          {/* ── STICKY FOOTER ── */}
          {!confirmed && (
            <div className="pb-booking-footer" style={{ flexShrink: 0, background: 'var(--pb-modal-card)', borderTop: '1px solid var(--pb-modal-border)', padding: '12px 20px 28px' }}>

              {/* Step 1 footer */}
              {step === 1 && (
                <div>
                  {selectedSvcs.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                      {selectedSvcs.map((s) => {
                        const svc = serviceById[s.serviceId];
                        if (!svc) return null;
                        return (
                          <div key={s.serviceId} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.06)', borderRadius: 20, padding: '4px 8px 4px 10px', border: '1px solid var(--pb-modal-border)' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pb-modal-text)' }}>{svc.name}</span>
                            <button onClick={() => toggleService(svc)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, lineHeight: 0 }}>
                              <X style={{ width: 11, height: 11, color: 'var(--pb-modal-muted)' }} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="pb-booking-footer-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: 0 }}>
                        {selectedSvcs.length > 0 ? `${selectedSvcs.length} servicio${selectedSvcs.length !== 1 ? 's' : ''} · ${formatDur(totalDuration)}` : 'Ningún servicio seleccionado'}
                      </p>
                      {selectedSvcs.length > 0 && <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--pb-modal-text)', margin: '2px 0 0' }}>{formatCurrency(totalPrice, orgLocale)}</p>}
                    </div>
                    <button disabled={selectedSvcs.length === 0} onClick={() => setStep(2)} style={{ background: selectedSvcs.length > 0 ? 'var(--pb-modal-accent)' : 'var(--pb-modal-border)', color: selectedSvcs.length > 0 ? 'white' : '#9c9890', borderRadius: 12, padding: '10px 20px', fontSize: 13, fontWeight: 700, border: 'none', cursor: selectedSvcs.length > 0 ? 'pointer' : 'not-allowed' }}>
                      Continuar →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2 footer — client identification */}
              {step === 2 && (
                <div className="pb-booking-footer-actions" style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setStep(1)} style={{ flex: 1, background: 'var(--pb-modal-card)', border: '1.5px solid #e8e5de', color: 'var(--pb-modal-text)', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Atrás</button>
                  <button disabled={!canContinueAfterClient} onClick={handleContinueAfterClient} style={{ flex: 2, background: canContinueAfterClient ? 'var(--pb-modal-accent)' : 'var(--pb-modal-border)', color: canContinueAfterClient ? 'white' : '#9c9890', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 700, border: 'none', cursor: canContinueAfterClient ? 'pointer' : 'not-allowed' }}>
                    Continuar →
                  </button>
                </div>
              )}

              {/* Step 3 footer — professional */}
              {step === 3 && (
                <div className="pb-booking-footer-actions" style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setStep(2)} style={{ flex: 1, background: 'var(--pb-modal-card)', border: '1.5px solid #e8e5de', color: 'var(--pb-modal-text)', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Atrás</button>
                  <button disabled={!selectedProId} onClick={() => setStep(4)} style={{ flex: 2, background: selectedProId ? 'var(--pb-modal-accent)' : 'var(--pb-modal-border)', color: selectedProId ? 'white' : '#9c9890', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 700, border: 'none', cursor: selectedProId ? 'pointer' : 'not-allowed' }}>Continuar →</button>
                </div>
              )}

              {/* Step 4 footer — date & time */}
              {step === 4 && (
                <div className="pb-booking-footer-actions" style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => { setBookingError(null); setStep(3); }} style={{ flex: 1, background: 'var(--pb-modal-card)', border: '1.5px solid #e8e5de', color: 'var(--pb-modal-text)', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Atrás</button>
                  <button disabled={selectedSlot === null} onClick={() => { setBookingError(null); setStep(5); }} style={{ flex: 2, background: selectedSlot !== null ? 'var(--pb-modal-accent)' : 'var(--pb-modal-border)', color: selectedSlot !== null ? 'white' : '#9c9890', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 700, border: 'none', cursor: selectedSlot !== null ? 'pointer' : 'not-allowed' }}>
                    Revisar resumen →
                  </button>
                </div>
              )}

              {/* Step 5 footer */}
              {step === 5 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* MP payment approved banner */}
                  {mpPaymentDone && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: '#166534', fontWeight: 600, textAlign: 'center' }}>
                      ✓ Pago aprobado — haz clic en Confirmar para finalizar tu reserva
                    </div>
                  )}
                  <div className="pb-booking-footer-actions" style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => {
                      if (reservationId) { void releaseSlotReservation(reservationId); setReservationId(null); setReservationExpiresAt(null); }
                      setStep(4);
                    }} style={{ flex: 1, background: 'var(--pb-modal-card)', border: '1.5px solid #e8e5de', color: 'var(--pb-modal-text)', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Atrás</button>
                    {depositRequired && !mpAvailable ? (
                      // Anticipo obligatorio sin cobro disponible: no se
                      // ofrece reservar. No se expone detalle técnico.
                      <div style={{ flex: 2, background: '#FDF2F2', border: '1.5px solid #F3C6C6', borderRadius: 12, padding: '10px 12px' }}>
                        <p style={{ color: '#8C2F2F', fontSize: 12, fontWeight: 600, margin: 0, textAlign: 'center' }}>
                          El pago online no está disponible temporalmente. Intenta nuevamente en unos minutos o contacta al negocio.
                        </p>
                      </div>
                    ) : depositRequired && mpAvailable && !mpPaymentDone ? (
                      <button
                        disabled={mpRedirecting}
                        onClick={() => void handleMpPayment()}
                        style={{ flex: 2, background: mpRedirecting ? 'var(--pb-modal-border)' : '#009ee3', color: mpRedirecting ? '#9c9890' : 'white', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 700, border: 'none', cursor: mpRedirecting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: mpRedirecting ? 0.7 : 1 }}
                      >
                        {mpRedirecting && <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} />}
                        {mpRedirecting ? 'Redirigiendo...' : `Pagar ${formatCurrency(effectiveDeposit, orgLocale)} con MercadoPago →`}
                      </button>
                    ) : (
                      <button disabled={booking} onClick={() => void handleBook()} style={{ flex: 2, background: '#1D9E75', color: 'white', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 700, border: 'none', cursor: booking ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: booking ? 0.7 : 1 }}>
                        {booking && <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} />}
                        {'Confirmar reserva →'}
                      </button>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}

          {showSuggestionModal && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 40 }}>
              <div style={{ width: '100%', maxWidth: 620, background: 'var(--pb-modal-card)', borderRadius: 24, border: '1px solid var(--pb-modal-border)', boxShadow: '0 24px 80px rgba(15,23,42,0.18)', padding: '24px 22px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--pb-modal-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Sugerencia inteligente</p>
                    <p style={{ fontSize: 28, fontWeight: 700, color: 'var(--pb-modal-text)', margin: '8px 0 6px', fontFamily: 'var(--pb-font-heading)' }}>¡Un momento! ✨</p>
                    <p style={{ fontSize: 14, color: 'var(--pb-modal-muted)', lineHeight: 1.6, margin: 0 }}>
                      {suggestionMode === 'repeat'
                        ? 'Antes de continuar, podrías complementar tu cita con servicios que ya te han gustado antes.'
                        : 'Antes de continuar, podrías complementar tu cita con otros servicios disponibles de categorías diferentes.'}
                    </p>
                  </div>
                  <button onClick={() => { setShowSuggestionModal(false); setStep(3); }} style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid var(--pb-modal-border)', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <X style={{ width: 16, height: 16, color: 'var(--pb-modal-muted)' }} />
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
                  {suggestedServices.map((svc) => {
                    const wasBookedBefore = (clientHistory?.service_visit_counts?.[svc.id] ?? 0) > 0;
                    return (
                      <div key={svc.id} style={{ borderRadius: 18, border: '1px solid var(--pb-modal-border)', background: 'white', padding: '16px 14px', boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 12, background: '#eefaf8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--pb-modal-accent)' }}>
                            {wasBookedBefore ? <Star style={{ width: 15, height: 15 }} /> : <Sparkles style={{ width: 15, height: 15 }} />}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--pb-modal-text)', margin: 0, lineHeight: 1.3 }}>{svc.name}</p>
                            <p style={{ fontSize: 11, color: 'var(--pb-modal-muted)', margin: '2px 0 0' }}>{svc.category}</p>
                          </div>
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--pb-modal-muted)', margin: '0 0 14px', minHeight: 34 }}>
                          {wasBookedBefore
                            ? `Ya lo has reservado ${clientHistory?.service_visit_counts?.[svc.id]} vez${(clientHistory?.service_visit_counts?.[svc.id] ?? 0) === 1 ? '' : 'es'}.`
                            : `Disponible online · ${formatDur(svc.duration_minutes)} · ${formatCurrency(svc.price, orgLocale)}`}
                        </p>
                        <button onClick={() => handleAddSuggestedService(svc)} style={{ width: '100%', background: 'white', color: 'var(--pb-modal-accent)', border: '1.5px solid var(--pb-modal-accent)', borderRadius: 999, padding: '10px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                          + Agregar
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button onClick={() => { setShowSuggestionModal(false); setStep(3); }} style={{ width: '100%', background: 'var(--pb-modal-accent)', color: 'white', border: 'none', borderRadius: 999, padding: '13px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    Continuar con mi selección →
                  </button>
                  <button onClick={() => { setShowSuggestionModal(false); setStep(1); }} style={{ width: '100%', background: 'transparent', color: 'var(--pb-modal-muted)', border: 'none', padding: '6px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Volver y editar mis servicios
                  </button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── PRO PROFILE PAGE ──────────────────────────────────────── */}
      {profilePro && (
        <ProProfilePage
          professional={profilePro}
          salonName={salon.name}
          themeVars={themeVars}
          onBack={() => setProfilePro(null)}
          onBook={(proId) => {
            setProfilePro(null);
            openSheet(false, proId);
          }}
        />
      )}
    </>
  );
}
